import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import axios from "axios";
import {
  Box,
  Heading,
  Text,
  Stack,
  Flex,
  Button,
  HStack,
  VStack,
  Icon,
  useToast,
  useDisclosure,
  Input,
  Badge,
  IconButton,
  Tag,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  FiUploadCloud,
  FiFileText,
  FiArrowRight,
  FiFile,
  FiArrowLeft,
  FiRotateCcw,
  FiAlertTriangle,
  FiCamera,
  FiNavigation,
  FiMapPin,
  FiCrosshair,
  FiClock,
  FiBookmark,
  FiCheck,
  FiSearch,
} from "react-icons/fi";
import WithSubnavigation from "./navbar";
import Sidebar from "./side";
import PharmacyMap from "../UI/PharmacyMap";
import MedicineSearchCard from "./UploadPrescription/MedicineSearchCard";
import { getMedId } from "./UploadPrescription/AddedMedicineList";
import MapPreviewInline from "./UploadPrescription/MapPreviewInline";
import PharmacyOfferCards from "./UploadPrescription/PharmacyOfferCards";
import PharmacySearchState from "./UploadPrescription/PharmacySearchState";

import { API_BASE } from "../../config";
const PRESCRIPTION_CHECKOUT_KEY = "prescriptionCheckout";
const PRESCRIPTION_DRAFT_KEY = "prescriptionDraft";
let inMemoryPrescriptionDraft = null;

const hasCookie = (name) => {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split(";")
    .map((entry) => entry.trim())
    .some((entry) => entry.startsWith(`${name}=`));
};

const getCoordPair = (value) => {
  if (Array.isArray(value) && value.length >= 2) {
    return { lng: Number(value[0]), lat: Number(value[1]) };
  }
  if (value && typeof value === "object") {
    return {
      lng: Number(value.lng ?? value.longitude ?? value[0]),
      lat: Number(value.lat ?? value.latitude ?? value[1]),
    };
  }
  return { lng: undefined, lat: undefined };
};

const getMedicineUnitPrice = (med) => {
  const raw = med?.mrp ?? med?.Price ?? med?.price ?? 0;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const buildExtractionDetails = (med) => {
  const details = [];
  if (med?.extracted_written_name && med?.extracted_written_name !== med?.extracted_name) {
    details.push(`Written: ${med.extracted_written_name}`);
  }
  if (med?.extracted_brand_name) details.push(`Brand: ${med.extracted_brand_name}`);
  if (med?.extracted_generic_name) details.push(`Generic: ${med.extracted_generic_name}`);
  if (med?.extracted_salt_name) details.push(`Salt: ${med.extracted_salt_name}`);
  if (med?.extracted_dosage_form) details.push(`Form: ${med.extracted_dosage_form}`);
  if (med?.extracted_strength) details.push(`Strength: ${med.extracted_strength}`);
  if (med?.extracted_dose_amount) details.push(`Dose: ${med.extracted_dose_amount}`);
  if (med?.extracted_route) details.push(`Route: ${med.extracted_route}`);
  if (med?.extracted_frequency) details.push(`Frequency: ${med.extracted_frequency}`);
  if (med?.extracted_duration) details.push(`Duration: ${med.extracted_duration}`);
  if (med?.extracted_quantity) details.push(`Quantity: ${med.extracted_quantity}`);
  if (med?.extracted_instruction) details.push(`Note: ${med.extracted_instruction}`);
  return details;
};

/* ─── Scanning animation ─────────────────────────────────────────────────── */
const ScanningAnimation = () => (
  <Box py={5} display="flex" flexDirection="column" alignItems="center" gap={3}>
    <style>{`
      @keyframes lp-scan { 0%,100%{top:8%;opacity:.55} 50%{top:82%;opacity:1} }
      @keyframes lp-dot { 0%,100%{transform:scale(.55);opacity:.35} 50%{transform:scale(1);opacity:1} }
      @keyframes lp-pill-float { 0%,100%{transform:translateY(0) rotate(-8deg);opacity:.7} 50%{transform:translateY(-8px) rotate(8deg);opacity:1} }
      @keyframes lp-pill-float2 { 0%,100%{transform:translateY(0) rotate(10deg);opacity:.6} 50%{transform:translateY(-6px) rotate(-10deg);opacity:.95} }
      @keyframes lp-glow { 0%,100%{opacity:.35;transform:scale(.95)} 50%{opacity:.7;transform:scale(1.05)} }
    `}</style>
    <Box position="relative" w="110px" h="140px">
      {/* Glow behind paper */}
      <Box position="absolute" w="80px" h="100px" left="15px" top="20px" borderRadius="12px"
        bg="radial-gradient(ellipse,rgba(15,118,110,0.18),transparent 70%)"
        style={{animation:"lp-glow 2s ease-in-out infinite"}} />
      {/* Prescription paper */}
      <Box position="absolute" left="15px" top="16px" w="80px" h="100px"
        bg="white" borderRadius="10px" border="1.5px solid #e2e8f0"
        boxShadow="0 6px 20px rgba(15,23,42,0.10)" overflow="hidden">
        {/* Rx header */}
        <Box bg="linear-gradient(90deg,#0f766e,#0d9488)" h="22px" display="flex" alignItems="center" px="8px">
          <Box as="span" fontSize="11px" fontWeight="900" color="white" letterSpacing=".04em">Rx</Box>
        </Box>
        {/* Paper lines */}
        {[32, 46, 58, 70, 82].map((t) => (
          <Box key={t} position="absolute" left="10px" right="10px" h="2px"
            bg={t === 46 ? "#99f6e4" : "#f1f5f9"} borderRadius="full" style={{top:`${t}%`}} />
        ))}
      </Box>
      {/* Scanning beam */}
      <Box position="absolute" left="15px" right="15px" h="2px"
        bg="linear-gradient(90deg,transparent,#0f766e 30%,#2dd4bf,#0f766e 70%,transparent)"
        borderRadius="full" style={{animation:"lp-scan 1.6s ease-in-out infinite",top:"8%"}}
      />
      {/* Floating pills */}
      <Box position="absolute" top="10px" left="0" w="18px" h="9px" borderRadius="full"
        bg="linear-gradient(90deg,#fb923c,#f97316)" boxShadow="0 2px 6px rgba(249,115,22,.35)"
        style={{animation:"lp-pill-flo  at 2.1s ease-in-out infinite"}} />
      <Box position="absolute" bottom="50px" right="0" w="16px" h="8px" borderRadius="full"
        bg="linear-gradient(90deg,#818cf8,#6366f1)" boxShadow="0 2px 6px rgba(99,102,241,.35)"
        style={{animation:"lp-pill-float2 2.5s ease-in-out .4s infinite"}} />
      <Box position="absolute" top="60px" right="2px" w="12px" h="12px" borderRadius="full"
        bg="linear-gradient(135deg,#34d399,#059669)" boxShadow="0 2px 6px rgba(5,150,105,.3)"
        style={{animation:"lp-pill-float 1.9s ease-in-out .8s infinite"}} />
    </Box>
    {/* Animated dots */}
    <Box display="flex" gap="6px" alignItems="center">
      {[0, .25, .5].map((d) => (
        <Box key={d} w="7px" h="7px" borderRadius="full" bg="#0f766e"
          style={{animation:`lp-dot 1.2s ease-in-out ${d}s infinite`}} />
      ))}
    </Box>
    <Text fontSize="xs" color="#475569" fontWeight="700" m={0} letterSpacing=".02em">
      Reading prescription…
    </Text>
  </Box>
);

/* ─── Onboarding Carousel ─────────────────────────────────────────────────── */

/* Illustrated SVG scenes for each carousel step */
const CarouselIllustration = ({ step }) => {
  if (step === 0) return ( /* Upload */
    <Box position="relative" w="100%" h="148px" display="flex" alignItems="center" justifyContent="center">
      <style>{`@keyframes lp-upload-bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
      @keyframes lp-upload-dash{0%{stroke-dashoffset:60}100%{stroke-dashoffset:0}}`}</style>
      <svg width="200" height="140" viewBox="0 0 200 140" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Shadow */}
        <ellipse cx="100" cy="128" rx="48" ry="8" fill="rgba(15,118,110,0.10)" />
        {/* Paper */}
        <g style={{animation:"lp-upload-bounce 2.4s ease-in-out infinite"}}>
          <rect x="55" y="20" width="90" height="108" rx="10" fill="white" stroke="#e2e8f0" strokeWidth="1.5"/>
          {/* Rx badge */}
          <rect x="55" y="20" width="90" height="26" rx="10" fill="url(#rxg)" />
          <defs><linearGradient id="rxg" x1="55" y1="20" x2="145" y2="46" gradientUnits="userSpaceOnUse">
            <stop stopColor="#0f766e"/><stop offset="1" stopColor="#0d9488"/>
          </linearGradient></defs>
          <text x="71" y="38" fontSize="13" fontWeight="900" fill="white" fontFamily="system-ui">Rx</text>
          {/* Lines */}
          {[58,68,78,88,98,108].map((y,i) => (
            <rect key={y} x="68" y={y} width={i%2===0?55:40} height="3" rx="1.5" fill={i===0?"#99f6e4":"#f1f5f9"}/>
          ))}
        </g>
        {/* Upload arrow */}
        <g transform="translate(82,10)">
          <circle cx="18" cy="18" r="18" fill="#f0fdfa"/>
          <polyline points="18,26 18,12" stroke="#0f766e" strokeWidth="2.5" strokeLinecap="round"/>
          <polyline points="12,18 18,12 24,18" stroke="#0f766e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        </g>
        {/* Dots */}
        <circle cx="40" cy="50" r="4" fill="#ccfbf1"/>
        <circle cx="165" cy="90" r="5" fill="#e0fdf4"/>
        <circle cx="34" cy="100" r="3" fill="#99f6e4"/>
      </svg>
    </Box>
  );
  if (step === 1) return ( /* AI extraction */
    <Box position="relative" w="100%" h="148px" display="flex" alignItems="center" justifyContent="center">
      <style>{`@keyframes lp-spark{0%,100%{opacity:.3;transform:scale(.7)}50%{opacity:1;transform:scale(1.15)}}
      @keyframes lp-pill-b{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}`}</style>
      <svg width="210" height="140" viewBox="0 0 210 140" fill="none">
        {/* Central AI circle */}
        <circle cx="105" cy="72" r="32" fill="url(#aig)" opacity=".95"/>
        <defs><radialGradient id="aig" cx="40%" cy="40%"><stop stopColor="#8b5cf6"/><stop offset="1" stopColor="#6d28d9"/></radialGradient></defs>
        <text x="94" y="77" fontSize="18" fontWeight="900" fill="white" fontFamily="system-ui">AI</text>
        {/* Orbit pills */}
        {[{x:40,y:40,c:"#fb923c",c2:"#f97316",d:0},{x:162,y:44,c:"#34d399",c2:"#059669",d:.5},{x:155,y:100,c:"#818cf8",c2:"#6366f1",d:.8},{x:42,y:100,c:"#f472b6",c2:"#db2777",d:1.2}].map((p,i)=>(
          <g key={i} style={{animation:`lp-pill-b 2s ease-in-out ${p.d}s infinite`}}>
            <rect x={p.x-14} y={p.y-7} width="28" height="14" rx="7" fill={`url(#pg${i})`}/>
            <defs><linearGradient id={`pg${i}`} x1={p.x-14} y1={p.y} x2={p.x+14} y2={p.y} gradientUnits="userSpaceOnUse">
              <stop stopColor={p.c}/><stop offset="1" stopColor={p.c2}/>
            </linearGradient></defs>
            <line x1={p.x} y1={p.y} x2={105} y2={72} stroke={p.c} strokeWidth="1" strokeDasharray="3 3" opacity=".4"/>
          </g>
        ))}
        {/* Sparkles */}
        {[{x:78,y:30,d:0},{x:140,y:28,d:.4},{x:170,y:70,d:.8},{x:35,y:72,d:1.2}].map((s,i)=>(
          <text key={i} x={s.x} y={s.y} fontSize="14" style={{animation:`lp-spark 1.8s ease-in-out ${s.d}s infinite`}} fill="#a78bfa">✦</text>
        ))}
      </svg>
    </Box>
  );
  if (step === 2) return ( /* Manual search */
    <Box position="relative" w="100%" h="148px" display="flex" alignItems="center" justifyContent="center">
      <style>{`@keyframes lp-cursor{0%,100%{opacity:1}50%{opacity:0}}`}</style>
      <svg width="210" height="140" viewBox="0 0 210 140" fill="none">
        {/* Search box */}
        <rect x="30" y="28" width="150" height="36" rx="18" fill="white" stroke="#e2e8f0" strokeWidth="1.5" filter="url(#sf)"/>
        <defs><filter id="sf" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="4" stdDeviation="6" floodOpacity=".07"/>
        </filter></defs>
        <circle cx="51" cy="46" r="8" stroke="#94a3b8" strokeWidth="2" fill="none"/>
        <line x1="57" y1="52" x2="63" y2="58" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"/>
        <text x="72" y="51" fontSize="11" fill="#94a3b8" fontFamily="system-ui">Dolo 650</text>
        <rect x="148" y="40" width="2" height="12" rx="1" fill="#0f766e" style={{animation:"lp-cursor 1s step-end infinite"}}/>
        {/* Result rows */}
        {[{y:80,w:120,name:"Dolo 650 Tablet",price:"₹30",c:"#0f766e"},{y:104,w:100,name:"Paracetamol 500mg",price:"₹12",c:"#475569"}].map((r,i)=>(
          <g key={i}>
            <rect x="30" y={r.y} width="150" height="22" rx="8" fill={i===0?"#f0fdfa":"#fafafa"} stroke={i===0?"#99f6e4":"#f1f5f9"} strokeWidth="1"/>
            <circle cx="44" cy={r.y+11} r="5" fill={r.c} opacity=".2"/>
            <circle cx="44" cy={r.y+11} r="3" fill={r.c}/>
            <text x="54" y={r.y+15} fontSize="10" fill="#0f172a" fontFamily="system-ui" fontWeight="600">{r.name}</text>
            <text x={r.w+50} y={r.y+15} fontSize="10" fill={r.c} fontFamily="system-ui" fontWeight="700">{r.price}</text>
          </g>
        ))}
      </svg>
    </Box>
  );
  if (step === 3) return ( /* Compare pharmacies */
    <Box position="relative" w="100%" h="148px" display="flex" alignItems="center" justifyContent="center">
      <style>{`@keyframes lp-pin{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
      @keyframes lp-ripple{0%{r:4;opacity:.8}100%{r:16;opacity:0}}`}</style>
      <svg width="210" height="140" viewBox="0 0 210 140" fill="none">
        {/* Map background */}
        <rect x="20" y="14" width="170" height="112" rx="14" fill="#f0fdf4" stroke="#e2e8f0" strokeWidth="1.5"/>
        {/* Roads */}
        <path d="M20 72 H190" stroke="#e2e8f0" strokeWidth="6"/>
        <path d="M105 14 V126" stroke="#e2e8f0" strokeWidth="6"/>
        <path d="M50 14 L20 72 L50 126" stroke="#f1f5f9" strokeWidth="3" fill="none"/>
        <path d="M160 14 L190 72 L160 126" stroke="#f1f5f9" strokeWidth="3" fill="none"/>
        {/* Pharmacies */}
        {[{x:70,y:52,d:0,best:true},{x:140,y:88,d:.6,best:false},{x:105,y:44,d:1,best:false}].map((p,i)=>(
          <g key={i} style={{animation:`lp-pin 2s ease-in-out ${p.d}s infinite`}}>
            <circle cx={p.x} cy={p.y+4} r="8" fill={p.best?"#0f766e":"#94a3b8"} opacity=".15"/>
            <path d={`M${p.x} ${p.y-16} C${p.x-8} ${p.y-16} ${p.x-10} ${p.y-8} ${p.x} ${p.y+2} C${p.x+10} ${p.y-8} ${p.x+8} ${p.y-16} ${p.x} ${p.y-16}Z`}
              fill={p.best?"#0f766e":"#94a3b8"}/>
            <circle cx={p.x} cy={p.y-10} r="4" fill="white"/>
            {p.best && <text x={p.x+12} y={p.y-8} fontSize="9" fontWeight="700" fill="#0f766e" fontFamily="system-ui">Best</text>}
          </g>
        ))}
        {/* Ripple on best */}
        <circle cx="70" cy="56" fill="none" stroke="#0f766e" strokeWidth="1.5">
          <animate attributeName="r" from="4" to="18" dur="2s" repeatCount="indefinite"/>
          <animate attributeName="opacity" from=".7" to="0" dur="2s" repeatCount="indefinite"/>
        </circle>
      </svg>
    </Box>
  );
  /* Delivery */
  return (
    <Box position="relative" w="100%" h="148px" display="flex" alignItems="center" justifyContent="center">
      <style>{`@keyframes lp-scoot{0%{transform:translateX(-30px)}100%{transform:translateX(30px)}}
      @keyframes lp-wheel{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
      <svg width="220" height="140" viewBox="0 0 220 140" fill="none">
        {/* Road */}
        <rect x="10" y="100" width="200" height="6" rx="3" fill="#e2e8f0"/>
        <rect x="10" y="101" width="200" height="2" rx="1" fill="#f8fafc"/>
        {/* Dashes */}
        {[40,80,120,160].map(x=>(<rect key={x} x={x} y="102" width="20" height="2" rx="1" fill="white" opacity=".7"/>))}
        {/* Scooter group */}
        <g style={{animation:"lp-scoot 2.5s ease-in-out infinite alternate"}}>
          {/* Body */}
          <path d="M88 96 Q90 78 104 78 L128 78 Q138 78 140 88 L140 96 Z" fill="#0f766e"/>
          <path d="M104 78 L108 66 L120 66 L120 78Z" fill="#0d9488"/>
          {/* Medicine box */}
          <rect x="113" y="58" width="20" height="14" rx="3" fill="white" stroke="#0f766e" strokeWidth="1.5"/>
          <text x="118" y="69" fontSize="9" fontWeight="900" fill="#0f766e" fontFamily="system-ui">Rx</text>
          {/* Windshield */}
          <path d="M108 78 L110 68 L118 68 L118 78Z" fill="#bfdbfe" opacity=".7"/>
          {/* Wheels */}
          <circle cx="96" cy="98" r="10" fill="#1e293b"/>
          <circle cx="96" cy="98" r="5" fill="#475569" style={{transformOrigin:"96px 98px",animation:"lp-wheel .6s linear infinite"}}/>
          <circle cx="132" cy="98" r="10" fill="#1e293b"/>
          <circle cx="132" cy="98" r="5" fill="#475569" style={{transformOrigin:"132px 98px",animation:"lp-wheel .6s linear infinite"}}/>
          {/* Speed lines */}
          {[70,76,82].map((y,i)=>(<line key={y} x1={78-i*4} y1={y} x2={86-i*4} y2={y} stroke="#99f6e4" strokeWidth="2" strokeLinecap="round" opacity={.9-i*.2}/>))}
        </g>
        {/* House */}
        <g transform="translate(168,58)">
          <polygon points="20,0 40,20 0,20" fill="#e0fdf4"/>
          <rect x="8" y="20" width="24" height="22" rx="2" fill="#f0fdfa" stroke="#99f6e4" strokeWidth="1.5"/>
          <rect x="15" y="28" width="10" height="14" rx="2" fill="#0f766e"/>
        </g>
        {/* Stars/sparkles */}
        <text x="50" y="60" fontSize="12" fill="#34d399" style={{animation:"lp-spark 1.5s ease-in-out infinite"}}>✦</text>
        <text x="168" y="46" fontSize="10" fill="#a78bfa" style={{animation:"lp-spark 1.5s ease-in-out .5s infinite"}}>✦</text>
      </svg>
    </Box>
  );
};

const CAROUSEL_STEPS = [
  { badge: null,         badgeColor: null,    title: "Upload your prescription",     desc: "Take a photo or attach a PDF — our system reads it in seconds." },
  { badge: "AI-Powered", badgeColor: "#7c3aed", title: "AI extracts every medicine", desc: "Brand, generic, dosage, quantity — detected automatically. No manual typing." },
  { badge: null,         badgeColor: null,    title: "Or search manually",            desc: "Type a brand name or salt and add medicines one by one at any time." },
  { badge: "Live prices",badgeColor: "#2563eb", title: "Compare nearby pharmacies",  desc: "Get real-time availability and pricing from connected pharmacies near you." },
  { badge: null,         badgeColor: null,    title: "Get it delivered",              desc: "Choose the best match and have medicines at your door — fast." },
];

const OnboardingCarousel = () => {
  const [active, setActive] = React.useState(0);
  const total = CAROUSEL_STEPS.length;

  React.useEffect(() => {
    const t = setInterval(() => setActive((s) => (s + 1) % total), 3800);
    return () => clearInterval(t);
  }, [total]);

  const step = CAROUSEL_STEPS[active];

  return (
    <Box pb={4} px={2} textAlign="center" userSelect="none">
      {/* Illustrated scene */}
      <Box overflow="hidden">
        <CarouselIllustration step={active} />
      </Box>

      {/* Badge */}
      <Box minH="22px" mb={1.5}>
        {step.badge && (
          <Box
            as="span"
            display="inline-block"
            fontSize="10px"
            fontWeight="700"
            color={step.badgeColor || "#0f766e"}
            bg={step.badgeColor === "#7c3aed" ? "#ede9fe" : step.badgeColor === "#2563eb" ? "#dbeafe" : "#f0fdfa"}
            px={2.5} py="3px"
            borderRadius="full"
            letterSpacing="0.04em"
          >
            ✦ {step.badge}
          </Box>
        )}
      </Box>

      {/* Text */}
      <Box minH="64px">
        <Text fontWeight="800" color="#0f172a" fontSize="sm" m={0} mb={1} letterSpacing="-0.01em">
          {step.title}
        </Text>
        <Text fontSize="xs" color="#64748b" m={0} lineHeight="1.6" maxW="240px" mx="auto">
          {step.desc}
        </Text>
      </Box>

      {/* Step dots */}
      <Flex justify="center" gap={1.5} mt={3}>
        {CAROUSEL_STEPS.map((_, i) => (
          <Box
            key={i}
            as="button"
            h="5px"
            w={active === i ? "20px" : "5px"}
            borderRadius="full"
            bg={active === i ? "#0f766e" : "#e2e8f0"}
            transition="all 0.3s ease"
            cursor="pointer"
            onClick={() => setActive(i)}
            border="none"
          />
        ))}
      </Flex>
    </Box>
  );
};

const UploadPrescription = ({ embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cookies, removeCookie] = useCookies([]);
  const [username, setUsername] = useState("");
  const [fullUser, setFullUser] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const mapViewportRef = useRef(null);
  const [measuredMapHeight, setMeasuredMapHeight] = useState(460);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [files, setFiles] = useState([]);
  const [selection, setSelection] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [addedMeds, setAddedMeds] = useState([]);
  const locationModal = useDisclosure();
  const [editedPharmacyName, setEditedPharmacyName] = useState("");
  const [editedPharmacyAddress, setEditedPharmacyAddress] = useState("");
  const [editedAddress, setEditedAddress] = useState("");
  const [editedDeliveryCoords, setEditedDeliveryCoords] = useState(null);
  const [editedPharmacyCoords, setEditedPharmacyCoords] = useState(null);
  const [editedPharmacyId, setEditedPharmacyId] = useState(null);
  const [nearbyPharmacies, setNearbyPharmacies] = useState([]);
  const [nearbyPharmaciesResolved, setNearbyPharmaciesResolved] = useState(false);
  const [searchRadiusKm, setSearchRadiusKm] = useState(5);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [pharmacySearch, setPharmacySearch] = useState("");
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [, setDeliveryQuoteMeta] = useState(null);
  const [, setDeliveryOptionsLoading] = useState(false);
  const [, setDeliveryOptionsError] = useState("");
  const [selectedDeliveryOptionId, setSelectedDeliveryOptionId] = useState("same_day");
  const [scanSuggestions, setScanSuggestions] = useState([]);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState("");
  const [scanNotice, setScanNotice] = useState("");
  const cameraModal = useDisclosure();
  const videoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const pharmacySectionRef = useRef(null);
  const [cameraError, setCameraError] = useState("");
  const [isCameraStarting, setIsCameraStarting] = useState(false);
  const [pharmacyMatches, setPharmacyMatches] = useState([]);
  const [pharmacyMatchesLoading, setPharmacyMatchesLoading] = useState(false);
  const [pharmacyMatchesError, setPharmacyMatchesError] = useState("");
  const [hasAttemptedPharmacyCheck, setHasAttemptedPharmacyCheck] = useState(false);
  const [dropZoneOpen, setDropZoneOpen] = useState(false);
  const [modalPlaceQuery, setModalPlaceQuery] = useState("");
  const [modalPlaceSuggestions, setModalPlaceSuggestions] = useState([]);
  const [modalPlacesLoading, setModalPlacesLoading] = useState(false);
  const [modalDetectingGps, setModalDetectingGps] = useState(false);
  const [modalSavingAddress, setModalSavingAddress] = useState(false);
  const [modalLastSelectedAddr, setModalLastSelectedAddr] = useState(null);
  const modalPlaceDebounce = useRef(null);
  const modalAddressInputRef = useRef(null);
  const toast = useToast();
  const handleSidebarToggle = () => {
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      setMobileSidebarOpen(true);
      return;
    }
    setSidebarCollapsed((prev) => !prev);
  };

  useEffect(() => {
    const node = mapViewportRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;

    const updateHeight = () => {
      const next = Math.max(320, Math.round(node.getBoundingClientRect().height || 0));
      if (next) {
        setMeasuredMapHeight((prev) => (prev === next ? prev : next));
      }
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const theme = {
    primary: "#0f766e",
    surface: "#f8fafc",
    accent: "#2563eb",
    panel: "#ffffff",
    brandName: "Local Pharma",
  };
  const medicineSubtotal = useMemo(
    () =>
      addedMeds.reduce(
        (sum, med) => sum + getMedicineUnitPrice(med) * Number(med?.qty || 1),
        0
      ),
    [addedMeds]
  );

  useEffect(() => {
    const verify = async () => {
      const hasToken = Boolean(cookies.token || hasCookie("token"));
      if (!hasToken) {
        navigate("/home", { replace: true });
        return;
      }
      try {
        const { data } = await axios.post(
          API_BASE,
          {},
          { withCredentials: true }
        );
        const { status, user } = data;
        setUsername(user);
        if (status) {
          const fullUserData = await axios.get(
            `${API_BASE}/patient/myInfo/${user}`
          );
          setFullUser(fullUserData.data);
        } else {
          removeCookie("token", { path: "/" });
          navigate("/home", { replace: true });
        }
      } catch (err) {
        // Only force logout on explicit 401/403; otherwise stay and allow retry
        const code = err?.response?.status;
        if (code === 401 || code === 403) {
          removeCookie("token", { path: "/" });
          navigate("/home", { replace: true });
        }
      } finally {
        setCheckingAuth(false);
      }
    };
    verify();
  }, [cookies, navigate, removeCookie]);

  useEffect(() => {
    const stateData =
      location.state && Object.keys(location.state || {}).length
        ? location.state
        : null;
    if (stateData) {
      setSelection(stateData);
      localStorage.setItem(PRESCRIPTION_CHECKOUT_KEY, JSON.stringify(stateData));
      return;
    }
    const stored = localStorage.getItem(PRESCRIPTION_CHECKOUT_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Restore pharmacy-only fields; let geolocation always populate fresh
        // delivery address & coords. Prevents stale cached locations.
        setSelection({
          pharmacyId: parsed?.pharmacyId || null,
          pharmacyName: parsed?.pharmacyName || "",
          pharmacyAddress: parsed?.pharmacyAddress || "",
          pharmacyCoords: parsed?.pharmacyCoords || null,
        });
      } catch (err) {
        setSelection(null);
      }
    }
  }, [location.state]);

  useEffect(() => {
    const memoryDraft = inMemoryPrescriptionDraft;
    const storedDraftRaw = localStorage.getItem(PRESCRIPTION_DRAFT_KEY);
    let storedDraft = null;
    if (storedDraftRaw) {
      try {
        storedDraft = JSON.parse(storedDraftRaw);
      } catch (error) {
        storedDraft = null;
      }
    }

    const source = memoryDraft || storedDraft;
    if (!source) return;

    if (Array.isArray(source.addedMeds) && source.addedMeds.length) {
      setAddedMeds(source.addedMeds);
    }
    if (Array.isArray(source.scanSuggestions) && source.scanSuggestions.length) {
      setScanSuggestions(source.scanSuggestions);
    }
    if (typeof source.scanNotice === "string") {
      setScanNotice(source.scanNotice);
    }
    if (typeof source.scanError === "string") {
      setScanError(source.scanError);
    }
    if (typeof source.searchRadiusKm === "number") {
      setSearchRadiusKm(source.searchRadiusKm);
    }
    if (typeof source.hasAttemptedPharmacyCheck === "boolean") {
      setHasAttemptedPharmacyCheck(source.hasAttemptedPharmacyCheck);
    }
    if (Array.isArray(source.pharmacyMatches) && source.pharmacyMatches.length) {
      setPharmacyMatches(source.pharmacyMatches);
    }
    if (Array.isArray(source.nearbyPharmacies) && source.nearbyPharmacies.length) {
      setNearbyPharmacies(source.nearbyPharmacies);
      setNearbyPharmaciesResolved(true);
    }
    if (Array.isArray(memoryDraft?.files) && memoryDraft.files.length) {
      setFiles(memoryDraft.files);
    }
  }, []);

  // Pre-select pharmacy when navigated from Home cart "Finalize Order"
  useEffect(() => {
    const state = location?.state;
    if (!state?.preselectedPharmacy) return;
    const p = state.preselectedPharmacy;
    setSelection((prev) => ({
      ...(prev || {}),
      pharmacyId: p.pharmacyId,
      pharmacyName: p.name,
      pharmacyAddress: p.address,
      pharmacyCoords: p.location?.coordinates || null,
    }));
    // Also pre-populate addedMeds from cartItems if provided
    if (Array.isArray(state.cartItems) && state.cartItems.length) {
      setAddedMeds((prev) => {
        if (prev.length) return prev;
        return state.cartItems.map((ci) => ({
          product_id: ci.productId || ci.product_id || String(Math.random()),
          product_name: ci.name || ci.product_name || "Medicine",
          manufacturer: ci.manufacturer || "",
          pack_size: ci.pack_size || "",
          composition: ci.composition || "",
          quantity: ci.quantity || 1,
          mrp: ci.price || 0,
        }));
      });
    }
    // Clear state so refresh doesn't re-apply
    window.history.replaceState({}, "");
  }, [location?.state]);

  useEffect(() => {
    if (!selection) return;
    localStorage.setItem(PRESCRIPTION_CHECKOUT_KEY, JSON.stringify(selection));
  }, [selection]);

  useEffect(() => {
    const serializableDraft = {
      addedMeds,
      scanSuggestions,
      scanNotice,
      scanError,
      searchRadiusKm,
      hasAttemptedPharmacyCheck,
      pharmacyMatches,
      nearbyPharmacies,
      filesMeta: files.map((file) => ({
        name: file.name,
        size: file.size,
        type: file.type,
      })),
    };
    inMemoryPrescriptionDraft = {
      ...serializableDraft,
      files,
    };
    localStorage.setItem(PRESCRIPTION_DRAFT_KEY, JSON.stringify(serializableDraft));
  }, [
    addedMeds,
    scanSuggestions,
    scanNotice,
    scanError,
    searchRadiusKm,
    hasAttemptedPharmacyCheck,
    pharmacyMatches,
    nearbyPharmacies,
    files,
  ]);

  const reverseGeocode = useCallback(async (coords) => {
    const [lng, lat] = coords || [];
    if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return "";
    try {
      const { data } = await axios.get(`${API_BASE}/api/mappls/rev-geocode`, {
        params: { lat, lng },
      });
      return (
        data?.display_name ||
        data?.formatted_address ||
        data?.results?.[0]?.formatted_address ||
        ""
      );
    } catch (error) {
      return "";
    }
  }, []);

  useEffect(() => {
    if (checkingAuth) return;

    const applyLocation = (coords, address) => {
      setSelection((prev) => ({
        pharmacyId: prev?.pharmacyId || null,
        pharmacyName: prev?.pharmacyName || "",
        pharmacyAddress: prev?.pharmacyAddress || "",
        pharmacyCoords: prev?.pharmacyCoords || null,
        ...prev,
        deliveryAddress: address,
        deliveryCoords: coords,
      }));
      localStorage.setItem("pillstackLocation", address);
      if (coords) localStorage.setItem("pillstackLocationCoords", JSON.stringify(coords));
      window.dispatchEvent(new Event("pillstackLocationChanged"));
    };

    // If user has saved addresses, use the first one instead of GPS
    const savedAddrs = fullUser?.DeliveryAddress;
    if (Array.isArray(savedAddrs) && savedAddrs.length > 0) {
      const first = savedAddrs[0];
      const addrText = first?.address || first;
      const addrCoords = Array.isArray(first?.coords) && first.coords.length === 2 ? first.coords : null;
      applyLocation(addrCoords, addrText);
      return;
    }

    // Otherwise fall back to GPS detection
    if (!navigator.geolocation) {
      toast({
        title: "Location not supported",
        description: "Your browser doesn't support geolocation. Please set your delivery address manually.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const coords = [position.coords.longitude, position.coords.latitude];
        const address = (await reverseGeocode(coords)) || "Current location";
        applyLocation(coords, address);
      },
      (error) => {
        const denied = error?.code === error?.PERMISSION_DENIED;
        toast({
          title: denied ? "Location access blocked" : "Couldn't detect location",
          description: denied
            ? "Click the lock icon in your browser address bar to allow location, or set the delivery address manually."
            : "Please set your delivery address manually.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
    // run once after auth completes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkingAuth]);

  // Sync when navbar location pill changes (user picks address via Google Places / saved / GPS)
  useEffect(() => {
    const onNavbarLocationChange = () => {
      const address = localStorage.getItem("pillstackLocation");
      const rawCoords = localStorage.getItem("pillstackLocationCoords");
      let coords = null;
      try { coords = rawCoords ? JSON.parse(rawCoords) : null; } catch { coords = null; }
      if (address) {
        setSelection((prev) => ({
          ...(prev || {}),
          deliveryAddress: address,
          ...(coords ? { deliveryCoords: coords } : {}),
        }));
      }
    };
    window.addEventListener("pillstackLocationChanged", onNavbarLocationChange);
    return () => window.removeEventListener("pillstackLocationChanged", onNavbarLocationChange);
  }, []);

  useEffect(() => {
    const fetchDeliveryOptions = async () => {
      if (!selection?.pharmacyName || !selection?.deliveryAddress) {
        setDeliveryOptions([]);
        setDeliveryQuoteMeta(null);
        setDeliveryOptionsError("");
        return;
      }

      const pickupCoords = getCoordPair(selection?.pharmacyCoords || selection?.coords || null);
      const dropCoords = getCoordPair(selection?.deliveryCoords || null);

      try {
        setDeliveryOptionsLoading(true);
        setDeliveryOptionsError("");
        const { data } = await axios.post(
          `${API_BASE}/order/delivery-options/quote`,
          {
            pickupAddress: selection.pharmacyAddress || selection.pharmacyName || "",
            deliveryAddress: selection.deliveryAddress,
            pickupCoords,
            deliveryCoords: dropCoords,
            orderSubtotal: Number(medicineSubtotal.toFixed(2)),
          },
          { withCredentials: true }
        );

        const options = Array.isArray(data?.options) ? data.options : [];
        setDeliveryOptions(options);
        setDeliveryQuoteMeta(data?.quote ? { ...data.quote, source: data.source || "borzo" } : null);
        if (options.length) {
          setSelectedDeliveryOptionId((prev) =>
            options.some((option) => option.id === prev) ? prev : options[0].id
          );
        }
      } catch (err) {
        setDeliveryOptions([]);
        setDeliveryQuoteMeta(null);
        setDeliveryOptionsError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Could not fetch delivery options right now."
        );
      } finally {
        setDeliveryOptionsLoading(false);
      }
    };

    fetchDeliveryOptions();
  }, [selection, medicineSubtotal]);

  const handleLogout = () => {
    removeCookie("token", { path: "/" });
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
    localStorage.removeItem(PRESCRIPTION_CHECKOUT_KEY);
    localStorage.removeItem(PRESCRIPTION_DRAFT_KEY);
    inMemoryPrescriptionDraft = null;
    navigate("/home", { replace: true });
  };

  const onFilesSelected = useCallback(
    async (fileList) => {
      if (!fileList?.length) return;
      const accepted = Array.from(fileList).filter((f) =>
        ["image/jpeg", "image/png", "application/pdf"].includes(f.type)
      );
      if (!accepted.length) {
        toast({
          title: "Only JPG, PNG, or PDF files are allowed",
          status: "warning",
          duration: 2500,
          isClosable: true,
          position: "bottom-right",
        });
        return;
      }
      setFiles(accepted);
      setScanSuggestions([]);
      setScanError("");
      setScanNotice("");
      setDropZoneOpen(false);
      toast({
        title: `${accepted.length} file${accepted.length > 1 ? "s" : ""} added`,
        status: "success",
        duration: 1800,
        isClosable: true,
        position: "bottom-right",
      });

      const form = new FormData();
      accepted.forEach((file) => form.append("prescriptions", file));
      try {
        setScanLoading(true);
        const { data } = await axios.post(`${API_BASE}/order/prescription/extract`, form, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        const suggestions = Array.isArray(data?.suggestions) ? data.suggestions : [];
        setScanSuggestions(suggestions);
        const autoAddableSuggestions = suggestions.filter((med) => med?.auto_add_eligible);
        const reviewCount = suggestions.length - autoAddableSuggestions.length;
        if (data?.fallback) {
          setScanNotice(data?.message || "Prescription scan is temporarily busy. You can continue by adding medicines manually.");
        } else if (suggestions.length) {
          const parts = [];
          if (autoAddableSuggestions.length) {
            parts.push(`${autoAddableSuggestions.length} medicine${autoAddableSuggestions.length > 1 ? "s" : ""} added automatically`);
          }
          if (reviewCount > 0) {
            parts.push(`${reviewCount} item${reviewCount > 1 ? "s" : ""} need review before adding`);
          }
          setScanNotice(parts.join(". "));
        } else {
          setScanNotice("");
        }

        if (autoAddableSuggestions.length) {
          setAddedMeds((prev) => {
            const next = [...prev];
            autoAddableSuggestions.forEach((med, index) => {
              const normalizedMed = med?.matched
                ? { ...med, qty: 1, extractedFromPrescription: true }
                : {
                    _id: `scan-${med?.extracted_name || "medicine"}-${index}`,
                    product_name: med?.extracted_name || "Scanned medicine",
                    name: med?.extracted_name || "Scanned medicine",
                    composition: med?.extracted_frequency || "",
                    extracted_written_name: med?.extracted_written_name || med?.extracted_name || "",
                    extracted_brand_name: med?.extracted_brand_name || "",
                    extracted_generic_name: med?.extracted_generic_name || "",
                    extracted_salt_name: med?.extracted_salt_name || "",
                    extracted_dosage_form: med?.extracted_dosage_form || "",
                    extracted_strength: med?.extracted_strength || "",
                    extracted_route: med?.extracted_route || "",
                    extracted_dose_amount: med?.extracted_dose_amount || "",
                    extracted_frequency: med?.extracted_frequency || "",
                    extracted_duration: med?.extracted_duration || "",
                    extracted_quantity: med?.extracted_quantity || "",
                    extracted_instruction: med?.extracted_instruction || "",
                    extracted_uncertain: Boolean(med?.extracted_uncertain),
                    prescription_required: true,
                    matched: false,
                    extractedFromPrescription: true,
                    qty: 1,
                  };
              const medId = getMedId(normalizedMed);
              const exists = next.some((item) => getMedId(item) === medId);
              if (!exists) {
                next.push(normalizedMed);
              }
            });
            return next;
          });
        }
      } catch (error) {
        setScanNotice("");
        setScanError(
          error?.response?.data?.message || "Could not scan this prescription right now. You can still add medicines manually."
        );
      } finally {
        setScanLoading(false);
      }
    },
    [toast]
  );

  const stopCameraStream = useCallback(() => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const closeCameraCapture = useCallback(() => {
    stopCameraStream();
    cameraModal.onClose();
  }, [cameraModal, stopCameraStream]);

  const capturePrescriptionPhoto = useCallback(() => {
    const videoEl = videoRef.current;
    if (!videoEl || videoEl.readyState < 2) {
      setCameraError("Camera preview is not ready yet.");
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = videoEl.videoWidth || 1280;
    canvas.height = videoEl.videoHeight || 720;
    const context = canvas.getContext("2d");
    context.drawImage(videoEl, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setCameraError("Could not capture the image.");
          return;
        }
        const capturedFile = new File(
          [blob],
          `prescription-${Date.now()}.jpg`,
          { type: "image/jpeg" }
        );
        onFilesSelected([capturedFile]);
        closeCameraCapture();
      },
      "image/jpeg",
      0.92
    );
  }, [closeCameraCapture, onFilesSelected]);

  const startCameraCapture = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast({
        title: "Camera not supported",
        description: "Your browser does not support direct camera capture.",
        status: "warning",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }

    const isMobile =
      /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
    const preferredFacingMode = isMobile ? "environment" : "user";
    const attempts = [
      {
        video: {
          facingMode: { ideal: preferredFacingMode },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      },
      {
        video: true,
        audio: false,
      },
    ];

    setCameraError("");
    setIsCameraStarting(true);
    stopCameraStream();

    try {
      let stream = null;
      let lastError = null;
      for (const constraints of attempts) {
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
          break;
        } catch (error) {
          lastError = error;
        }
      }

      if (!stream) {
        throw lastError || new Error("Could not start the camera.");
      }

      cameraStreamRef.current = stream;
      cameraModal.onOpen();
    } catch (error) {
      setCameraError(
        error?.message || "Camera access was denied or unavailable."
      );
      toast({
        title: "Could not open camera",
        description:
          error?.message || "Allow camera access and try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setIsCameraStarting(false);
    }
  }, [cameraModal, stopCameraStream, toast]);

  useEffect(() => {
    if (cameraModal.isOpen && videoRef.current && cameraStreamRef.current) {
      videoRef.current.srcObject = cameraStreamRef.current;
      videoRef.current
        .play()
        .catch(() => setCameraError("Could not start the live preview."));
    }
  }, [cameraModal.isOpen]);

  useEffect(() => () => stopCameraStream(), [stopCameraStream]);

  const fetchNearbyPharmacies = useCallback(async (coords) => {
    if (!coords || coords.length !== 2) return;
    setNearbyLoading(true);
    setNearbyPharmaciesResolved(false);
    try {
      const [lat, lng] = [coords[1], coords[0]];
      const res = await axios.post(
        `${API_BASE}/pharmacist/nearby`,
        {
          latitude: lat,
          longitude: lng,
          distanceRangeKm: searchRadiusKm,
          limit: 30,
        },
        { withCredentials: true }
      );
      const list = res.data?.pharmacies || [];
      const mapped = list
        .map((p) => ({
          id: p._id || p.Username || p.Email || p.Name,
          name: p.Name || "Pharmacy",
          address: p.address || p.city || "",
          coords: Array.isArray(p.location?.coordinates)
            ? p.location.coordinates
            : null,
          distanceKm: p.distanceKm ? p.distanceKm.toFixed(1) : null,
          isFavorite: Boolean(p.isFavorite),
          isPreferred: Boolean(p.isPreferred),
          isOpen: Boolean(p.isOpen),
          openStatus: p.openStatus || "",
        }))
        .filter((p) => p.id && p.coords);
      setNearbyPharmacies(mapped);
      setNearbyPharmaciesResolved(true);
    } catch (err) {
      setNearbyPharmacies([]);
      setNearbyPharmaciesResolved(true);
    } finally {
      setNearbyLoading(false);
    }
  }, [searchRadiusKm]);

  const resetUpload = useCallback(() => {
    setFiles([]);
    setScanSuggestions([]);
    setScanError("");
    setDropZoneOpen(true);
  }, []);

  // --- Modal Google Places helpers ---
  const modalSearchPlaces = useCallback((query) => {
    if (modalPlaceDebounce.current) clearTimeout(modalPlaceDebounce.current);
    if (!query || query.trim().length < 2) {
      setModalPlaceSuggestions([]);
      return;
    }
    modalPlaceDebounce.current = setTimeout(async () => {
      setModalPlacesLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE}/api/google/places-autocomplete`, {
          params: { input: query.trim() },
        });
        setModalPlaceSuggestions(data?.predictions || []);
      } catch {
        setModalPlaceSuggestions([]);
      } finally {
        setModalPlacesLoading(false);
      }
    }, 300);
  }, []);

  const modalSelectPlace = useCallback(async (placeId, description) => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/google/place-details`, {
        params: { place_id: placeId },
      });
      const address = data?.formatted_address || description;
      const coords = data?.lat && data?.lng ? [data.lng, data.lat] : null;
      setEditedAddress(address);
      if (coords) setEditedDeliveryCoords(coords);
      setModalLastSelectedAddr(address);
    } catch {
      setEditedAddress(description);
      setModalLastSelectedAddr(description);
    }
    setModalPlaceQuery("");
    setModalPlaceSuggestions([]);
  }, []);

  const modalSelectSavedAddress = useCallback((addr) => {
    setEditedAddress(addr);
    setModalPlaceQuery("");
    setModalPlaceSuggestions([]);
    setModalLastSelectedAddr(null);
  }, []);

  const modalResetToGps = useCallback(() => {
    if (!navigator.geolocation) return;
    setModalDetectingGps(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords = [pos.coords.longitude, pos.coords.latitude];
        try {
          const { data } = await axios.get(`${API_BASE}/api/mappls/rev-geocode`, {
            params: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          });
          const address =
            data?.display_name ||
            data?.formatted_address ||
            data?.results?.[0]?.formatted_address ||
            "Current location";
          setEditedAddress(address);
          setEditedDeliveryCoords(coords);
        } catch {
          setEditedAddress("Current location");
          setEditedDeliveryCoords(coords);
        }
        setModalDetectingGps(false);
        setModalPlaceQuery("");
        setModalPlaceSuggestions([]);
      },
      () => {
        setModalDetectingGps(false);
        toast({ title: "Couldn't detect location", status: "warning", duration: 3000, isClosable: true });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [toast]);

  const modalSaveAddress = useCallback(async (addr) => {
    if (!addr || modalSavingAddress) return;
    setModalSavingAddress(true);
    try {
      await axios.post(
        `${API_BASE}/patient/addDeliveryAddress`,
        { address: addr },
        { withCredentials: true }
      );
      toast({ title: "Address saved", status: "success", duration: 2500, isClosable: true });
      setModalLastSelectedAddr(null);
    } catch {
      toast({ title: "Couldn't save address", status: "error", duration: 2500, isClosable: true });
    } finally {
      setModalSavingAddress(false);
    }
  }, [modalSavingAddress, toast]);

  const modalIsAddrSaved = useCallback((addr) => {
    if (!addr) return true;
    const saved = fullUser?.DeliveryAddress || [];
    return saved.some((s) => s.toLowerCase().trim() === addr.toLowerCase().trim());
  }, [fullUser]);

  useEffect(() => {
    if (locationModal.isOpen) {
      setEditedPharmacyName(selection?.pharmacyName || "");
      setEditedPharmacyAddress(selection?.pharmacyAddress || "");
      setEditedAddress(selection?.deliveryAddress || "");
      setModalPlaceQuery("");
      setModalPlaceSuggestions([]);
      setModalLastSelectedAddr(null);
      setEditedDeliveryCoords(
        selection?.deliveryCoords || selection?.coords || null
      );
      setEditedPharmacyCoords(
        selection?.pharmacyCoords || selection?.coords || null
      );
      setEditedPharmacyId(
        selection?.pharmacyId || selection?.pharmacy?.id || null
      );
      const baseCoords =
        selection?.deliveryCoords ||
        selection?.coords ||
        (Array.isArray(selection?.pharmacyCoords)
          ? selection.pharmacyCoords
          : null);
      if (baseCoords) {
        fetchNearbyPharmacies(baseCoords);
      } else {
        setNearbyPharmacies([]);
      }
    }
  }, [fetchNearbyPharmacies, locationModal.isOpen, selection]);

  const handleDrop = (e) => {
    e.preventDefault();
    onFilesSelected(e.dataTransfer.files);
  };

  const handleFileInput = (e) => {
    onFilesSelected(e.target.files);
    e.target.value = "";
  };

  const isPrescriptionRequired = (med) => {
    const flag = med?.prescription_required || med?.prescriptionRequired;
    return typeof flag === "string"
      ? flag.toLowerCase().includes("prescription")
      : Boolean(flag);
  };

  const requiresRxButNoUpload = useMemo(
    () =>
      addedMeds.some((m) => isPrescriptionRequired(m)) && files.length === 0,
    [addedMeds, files.length]
  );
  const hasRxMeds = useMemo(
    () => addedMeds.some((m) => isPrescriptionRequired(m)),
    [addedMeds]
  );
  const totalMedicineUnits = useMemo(
    () => addedMeds.reduce((sum, med) => sum + Number(med?.qty || 1), 0),
    [addedMeds]
  );
  const rxMedicineCount = useMemo(
    () => addedMeds.filter((med) => isPrescriptionRequired(med)).length,
    [addedMeds]
  );
  const savedPrescriptions = useMemo(() => {
    const orders = Array.isArray(fullUser?.Orders) ? fullUser.Orders : [];
    return orders
      .filter((order) => order?.isPrescriptionOrder || order?.workflowStage || order?.prescriptionFiles?.length)
      .slice(0, 4)
      .map((order, index) => ({
        id: order?._id || `rx-${index}`,
        label: order?.pharmacyName || order?.Status || "Previous prescription",
        date: order?.createdAt
          ? new Date(order.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
          : "Recent",
        items: Array.isArray(order?.items) ? order.items.length : null,
      }));
  }, [fullUser]);

  const [prescriptionHistory, setPrescriptionHistory] = useState([]);
  const [prescriptionHistoryLoading, setPrescriptionHistoryLoading] = useState(false);
  const [prescriptionHistoryLoaded, setPrescriptionHistoryLoaded] = useState(false);

  const fetchPrescriptionHistory = useCallback(async () => {
    if (prescriptionHistoryLoaded) return;
    setPrescriptionHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/order/prescription/history`, { withCredentials: true });
      setPrescriptionHistory(Array.isArray(data) ? data : []);
      setPrescriptionHistoryLoaded(true);
    } catch (e) {
      setPrescriptionHistory([]);
    } finally {
      setPrescriptionHistoryLoading(false);
    }
  }, [prescriptionHistoryLoaded]);

  const handleUseSavedPrescription = useCallback(async (savedFile) => {
    const fileUrl = `${API_BASE}/${savedFile.path.replace(/^\//, "")}`;
    try {
      const resp = await fetch(fileUrl, { credentials: "include" });
      const blob = await resp.blob();
      const file = new File([blob], savedFile.originalName || savedFile.fileName, { type: savedFile.mimeType || blob.type || "image/jpeg" });
      await onFilesSelected([file]);
      toast({ title: "Prescription loaded from history", status: "success", duration: 1800, isClosable: true, position: "bottom-right" });
    } catch (e) {
      toast({ title: "Could not load saved prescription", status: "error", duration: 2400, isClosable: true, position: "bottom-right" });
    }
  }, [onFilesSelected, toast]);
  const hasDeliveryLocation = useMemo(() => {
    const coords = getCoordPair(selection?.deliveryCoords || selection?.coords || null);
    return Boolean(selection?.deliveryAddress) && Number.isFinite(coords.lat) && Number.isFinite(coords.lng);
  }, [selection]);
  const pharmacySelectionIsFromResults = useMemo(
    () =>
      Boolean(selection?.pharmacyId) &&
      pharmacyMatches.some((match) => match.pharmacyId === selection?.pharmacyId),
    [pharmacyMatches, selection?.pharmacyId]
  );
  const requestSummaryItems = useMemo(
    () => [
      {
        label: "Prescription",
        value: files.length ? `${files.length} uploaded` : "Best with Rx",
        tone: files.length ? "#dcfce7" : "#eef2ff",
        color: files.length ? "#86efac" : "#bfdbfe",
      },
      {
        label: "Medicines",
        value: `${addedMeds.length} added`,
        tone: addedMeds.length ? "#dffaf7" : "#f8fafc",
        color: addedMeds.length ? "#99f6e4" : "rgba(255,255,255,0.72)",
      },
      {
        label: "Location",
        value: hasDeliveryLocation ? "Ready" : "Set location",
        tone: hasDeliveryLocation ? "#dcfce7" : "#fff7ed",
        color: hasDeliveryLocation ? "#86efac" : "#fdba74",
      },
      {
        label: "Pharmacy",
        value: pharmacySelectionIsFromResults ? "Selected" : "Choose now",
        tone: pharmacySelectionIsFromResults ? "#dbeafe" : "#f8fafc",
        color: pharmacySelectionIsFromResults ? "#bfdbfe" : "rgba(255,255,255,0.72)",
      },
    ],
    [addedMeds.length, files.length, hasDeliveryLocation, pharmacySelectionIsFromResults]
  );

  const handleSearchChange = async (val) => {
    setSearchTerm(val);
    if (!val || val.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const resp = await axios.get(`${API_BASE}/api/medicines/by-name`, {
        params: { name: val.trim(), limit: 8, namesOnly: false },
      });
      setSearchResults(resp.data?.results || []);
    } catch (err) {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddMedicine = (med) => {
    if (!med) return;
    const id = getMedId(med);
    setAddedMeds((prev) => {
      const existing = prev.find((m) => getMedId(m) === id);
      if (existing) {
        return prev.map((m) =>
          getMedId(m) === id ? { ...m, qty: (m.qty || 1) + 1 } : m
        );
      }
      return [...prev, { ...med, qty: 1 }];
    });
    setSearchTerm("");
    setSearchResults([]);
    toast({
      title: "Added to order",
      status: "success",
      duration: 1600,
      isClosable: true,
      position: "bottom-right",
    });
  };

  const handleFindPharmacies = async () => {
    if (!addedMeds.length) {
      toast({
        title: "Add medicines first",
        description: "Upload a prescription or add medicines manually before checking pharmacies.",
        status: "warning",
        duration: 2400,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }

    if (requiresRxButNoUpload) {
      toast({
        title: "Prescription required",
        description: "Upload a prescription for Rx medicines before finding pharmacies.",
        status: "warning",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }

    const coords = selection?.deliveryCoords || selection?.coords || null;
    const { lat, lng } = getCoordPair(coords);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      toast({
        title: "Location not ready",
        description: "Allow current location or set a delivery address first.",
        status: "warning",
        duration: 2400,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }

    setHasAttemptedPharmacyCheck(true);
    try {
      setPharmacyMatchesLoading(true);
      setPharmacyMatchesError("");
      setSelection((prev) => ({
        ...(prev || {}),
        pharmacyId: null,
        pharmacyName: "",
        pharmacyAddress: "",
        pharmacyCoords: null,
      }));
      const { data } = await axios.post(
        `${API_BASE}/order/find-pharmacies`,
        {
          latitude: lat,
          longitude: lng,
          distanceRangeKm: searchRadiusKm,
          limit: 20,
          items: addedMeds,
        },
        { withCredentials: true }
        );
      const matches = Array.isArray(data?.pharmacies) ? data.pharmacies : [];
      setPharmacyMatches(matches);
      if (matches.length) {
        setNearbyPharmacies(
          matches.map((match) => ({
            id: match.pharmacyId,
            name: match.name,
            address: match.address,
            coords: match.location?.coordinates || [],
            distanceKm: match.distanceKm,
            matchPercent: match.matchPercent,
            totalAmount: match.totalAmount,
            savingsAmount: match.savingsAmount,
            unavailableItems: match.unavailableItems,
            items: match.items || [],
            isBestMatch: Boolean(match.isBestMatch),
          }))
        );
      }
    } catch (error) {
      setPharmacyMatches([]);
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      if (status === 401 || message === 'Unauthorized') {
        removeCookie("token", { path: "/" });
        navigate("/home", { replace: true });
        return;
      }
      setPharmacyMatchesError(
        message || "Could not check pharmacies right now."
      );
    } finally {
      setPharmacyMatchesLoading(false);
      setTimeout(() => {
        pharmacySectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  const handleChoosePharmacyOffer = (offer) => {
    if (!offer) return;
    setSelection((prev) => ({
      ...(prev || {}),
      pharmacyId: offer.pharmacyId,
      pharmacyName: offer.name,
      pharmacyAddress: offer.address,
      pharmacyCoords: offer.location?.coordinates || null,
    }));
    toast({
      title: "Pharmacy selected",
      description: `${offer.name} is now the active pharmacy for this order draft.`,
      status: "success",
      duration: 2200,
      isClosable: true,
      position: "bottom-right",
    });
  };

  const handleContinue = async () => {
    if (!addedMeds.length) {
      toast({
        title: "Add medicines first",
        status: "warning",
        duration: 2200,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }

    if (requiresRxButNoUpload) {
      toast({
        title: "Prescription required",
        description: "Upload a prescription for Rx-only items before confirming.",
        status: "error",
        duration: 2200,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }

    if (!selection?.pharmacyId || !selection?.deliveryAddress) {
      toast({
        title: "Select a pharmacy and delivery location first",
        description: "Go back to the map and pick where to fulfil this order.",
        status: "warning",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }

    const selectedDeliveryOption =
      deliveryOptions.find((option) => option.id === selectedDeliveryOptionId) ||
      null;
    const deliveryCoords = getCoordPair(selection?.deliveryCoords || selection?.coords || null);

    const form = new FormData();
    files.forEach((file) => form.append("prescriptions", file));
    form.append("pharmacyId", selection.pharmacyId);
    form.append("pharmacyName", selection.pharmacyName || "");
    const latVal = deliveryCoords.lat ?? "";
    const lngVal = deliveryCoords.lng ?? "";
    form.append("deliveryAddress", selection.deliveryAddress || editedAddress || "");
    form.append("deliveryFullAddress", editedAddress || selection.deliveryAddress || "");
    form.append("deliveryLat", latVal);
    form.append("deliveryLng", lngVal);
    form.append("selectedMedicines", JSON.stringify(addedMeds || []));
    if (selectedDeliveryOption) {
      form.append("deliveryServiceLevel", selectedDeliveryOption.id || "");
      form.append("deliveryServiceLabel", selectedDeliveryOption.label || "");
      form.append("deliveryServiceFee", String(selectedDeliveryOption.deliveryFee || 0));
      form.append("deliveryServiceAmount", String(selectedDeliveryOption.amount || 0));
      form.append(
        "deliveryOriginalFee",
        String(
          selectedDeliveryOption.originalDeliveryFee ||
            selectedDeliveryOption.deliveryFee ||
            0
        )
      );
      form.append(
        "deliveryConcessionAmount",
        String(selectedDeliveryOption.concessionAmount || 0)
      );
      form.append(
        "deliveryConcessionLabel",
        selectedDeliveryOption.concessionLabel || ""
      );
      form.append(
        "deliveryConcessionType",
        selectedDeliveryOption.concessionType || "none"
      );
      form.append(
        "deliveryIsFree",
        String(Boolean(selectedDeliveryOption.isFreeDelivery))
      );
      form.append("deliveryServiceCurrency", selectedDeliveryOption.currency || "INR");
      form.append("deliveryDistanceKm", String(selectedDeliveryOption.distanceKm || 0));
      form.append("deliveryEtaLabel", selectedDeliveryOption.etaLabel || "");
    }

    try {
      setIsSubmitting(true);
      const resp = await axios.post(`${API_BASE}/order/prescription`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const created = resp?.data;
      localStorage.removeItem(PRESCRIPTION_DRAFT_KEY);
      inMemoryPrescriptionDraft = null;
      toast({
        title: "Order confirmed",
        description: "Your prescription has been sent to the pharmacy.",
        status: "success",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
      navigate(
        created?._id
          ? `/order/prescription/${created._id}`
          : "/order/prescription/summary",
        {
          replace: true,
          state: {
            order: created || null,
            medicines: addedMeds,
            prescriptionCount: files.length,
          },
        }
      );
    } catch (err) {
      toast({
        title: "Could not place order",
        description: err?.response?.data?.message || "Please try again.",
        status: "error",
        duration: 2800,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const applyLocationChange = () => {
    if (!editedPharmacyName?.trim() || !editedAddress?.trim()) {
      toast({
        title: "Add pharmacy and address",
        description: "Please fill both pharmacy name and delivery address.",
        status: "warning",
        duration: 2400,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }
    const nextSelection = {
      ...selection,
      pharmacyName: editedPharmacyName.trim(),
      pharmacyAddress: editedPharmacyAddress.trim() || selection?.pharmacyAddress || "",
      deliveryAddress: editedAddress.trim(),
      pharmacyId:
        editedPharmacyId ||
        selection?.pharmacyId ||
        selection?.pharmacy?.id ||
        null,
      deliveryCoords:
        editedDeliveryCoords ||
        selection?.deliveryCoords ||
        selection?.coords ||
        null,
      pharmacyCoords:
        editedPharmacyCoords ||
        selection?.pharmacyCoords ||
        selection?.coords ||
        null,
    };
    setSelection(nextSelection);
    localStorage.setItem(PRESCRIPTION_CHECKOUT_KEY, JSON.stringify(nextSelection));
    locationModal.onClose();
    toast({
      title: "Selection updated",
      description: "Pharmacy and address updated for this order.",
      status: "success",
      duration: 2000,
      isClosable: true,
      position: "bottom-right",
    });
  };

  useEffect(() => {
    const coords = selection?.deliveryCoords || selection?.coords || null;
    if (Array.isArray(coords) && coords.length === 2) {
      fetchNearbyPharmacies(coords);
    }
  }, [fetchNearbyPharmacies, searchRadiusKm, selection?.coords, selection?.deliveryCoords]);

  useEffect(() => {
    const coords = selection?.deliveryCoords || selection?.coords || null;
    if (Array.isArray(coords) && coords.length === 2) {
      fetchNearbyPharmacies(coords);
    }
  }, [fetchNearbyPharmacies, selection?.coords, selection?.deliveryCoords]);

  const requestSummaryStrip = (
    <Box
      bg="rgba(15,23,42,0.96)"
      borderRadius="18px"
      px={{ base: 5, lg: 6 }}
      py={3.5}
      border="1px solid rgba(255,255,255,0.08)"
      boxShadow="0 18px 40px rgba(15,23,42,0.18)"
      position={{ base: "sticky", xl: "sticky" }}
      bottom="16px"
      zIndex={5}
    >
      <Flex align="center" gap={0} wrap={{ base: "wrap", md: "nowrap" }}>
        {requestSummaryItems.map((item, i) => (
          <React.Fragment key={item.label}>
            {i > 0 && (
              <Box
                w="1px" h="28px"
                bg="rgba(255,255,255,0.12)"
                mx={{ base: 4, md: 6 }}
                flexShrink={0}
                display={{ base: "none", sm: "block" }}
              />
            )}
            <Box flex="1" minW={{ base: "40%", sm: "auto" }} py={{ base: 1.5, md: 0 }}>
              <Text fontSize="10px" fontWeight="700" color="rgba(255,255,255,0.56)" textTransform="uppercase" letterSpacing="0.08em" m={0} mb="4px">
                {item.label}
              </Text>
              <Text fontSize="sm" fontWeight="700" color={item.color} m={0} letterSpacing="-0.01em">
                {item.value}
              </Text>
            </Box>
          </React.Fragment>
        ))}
        <Button
          size="sm"
          h="38px"
          px={4.5}
          bg="white"
          color="#0f172a"
          border="1px solid rgba(255,255,255,0.14)"
          leftIcon={<FiNavigation size={12} />}
          onClick={locationModal.onOpen}
          borderRadius="10px"
          fontWeight="800"
          fontSize="xs"
          _hover={{ bg: "#f8fafc" }}
          flexShrink={0}
          ml={{ base: 0, md: 4 }}
          mt={{ base: 2, md: 0 }}
        >
          Edit steps
        </Button>
      </Flex>
    </Box>
  );

  const addMedicinesSection = (
    <>
      <Box px={{ base: 5, lg: 6 }} py={{ base: 4, lg: 5 }}>
      <Flex direction="column" gap={{ base: 4, lg: 4 }}>
              {addedMeds.length > 0 && (
                <Flex align="center" justify="space-between" wrap="wrap" gap={3}>
                  <Text fontWeight="800" color="#0f172a" fontSize="md" m={0} letterSpacing="-0.01em">Medicines</Text>
                  <Box as="span" fontSize="12px" fontWeight="700" color="#0f766e" bg="#f0fdfa" px={3} py="5px" borderRadius="full" border="1px solid #99f6e4">
                    {addedMeds.length} added{rxMedicineCount > 0 ? ` · ${rxMedicineCount} Rx` : ""}
                  </Box>
                </Flex>
              )}

              <MedicineSearchCard
                searchTerm={searchTerm}
                isSearching={isSearching}
                searchResults={searchResults}
                handleSearchChange={handleSearchChange}
                handleAddMedicine={handleAddMedicine}
                addedMeds={addedMeds}
                setAddedMeds={setAddedMeds}
                isPrescriptionRequired={isPrescriptionRequired}
                hideSearch={embedded}
              />

      </Flex>
      </Box>

      <Box borderTop="1px solid #f1f5f9" px={{ base: 5, lg: 6 }} py={4}>
        <Button
          w="full"
          size="md"
          bg={!addedMeds.length || requiresRxButNoUpload ? "#f1f5f9" : "linear-gradient(135deg, #0f766e 0%, #0d9488 100%)"}
          color={!addedMeds.length || requiresRxButNoUpload ? "#94a3b8" : "white"}
          _hover={{
            bg: !addedMeds.length || requiresRxButNoUpload ? "#f1f5f9" : "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)",
            transform: !addedMeds.length || requiresRxButNoUpload ? "none" : "translateY(-1px)",
            boxShadow: !addedMeds.length || requiresRxButNoUpload ? "none" : "0 6px 20px rgba(15,118,110,0.35)",
          }}
          _active={{ transform: "none", boxShadow: "none" }}
          rightIcon={<FiArrowRight />}
          onClick={handleFindPharmacies}
          isLoading={pharmacyMatchesLoading}
          isDisabled={!addedMeds.length || requiresRxButNoUpload}
          borderRadius="10px"
          fontWeight="700"
          fontSize="sm"
          h="44px"
          letterSpacing="0.01em"
          transition="all 0.2s ease"
          boxShadow={addedMeds.length && !requiresRxButNoUpload ? "0 4px 14px rgba(15,118,110,0.25)" : "none"}
        >
          {!addedMeds.length ? "Add medicines to continue" : requiresRxButNoUpload ? "Upload prescription to continue" : "Check prices now"}
        </Button>
      </Box>
    </>
  );

  const pendingScanSuggestions = scanSuggestions.filter((med, index) => {
    const matched = Boolean(med?.matched);
    const tempMed = matched
      ? med
      : { _id: `scan-${med?.extracted_name || "medicine"}-${index}`, product_name: med?.extracted_name || "Scanned medicine" };
    return !addedMeds.some((item) => getMedId(item) === getMedId(tempMed));
  });
  const allScanAdded = scanSuggestions.length > 0 && pendingScanSuggestions.length === 0;

  const uploadSection = (
    <>
      {/* Header */}
      <Box px={{ base: 5, lg: 6 }} pt={{ base: 5, lg: 5 }} pb={2.5}>
        <Flex align="center" justify="space-between" gap={3}>
          <Box>
            <Text fontWeight="800" color="#0f172a" fontSize="md" m={0} letterSpacing="-0.01em">Prescription</Text>
            <Text fontSize="xs" color="#64748b" m={0}>
              {files.length ? `${files.length} file${files.length > 1 ? "s" : ""} attached` : "Upload once. AI reads, detects, and adds medicines automatically."}
            </Text>
          </Box>
          {hasRxMeds && files.length === 0 ? (
            <Box as="span" fontSize="11px" fontWeight="700" color="#c2410c" bg="#fff1f2" px={3} py="5px" borderRadius="full" border="1px solid #fecdd3">
              Required
            </Box>
          ) : files.length ? (
            <Box as="span" fontSize="11px" fontWeight="700" color="#0f766e" bg="#f0fdfa" px={3} py="5px" borderRadius="full" border="1px solid #99f6e4">
              {scanLoading ? "Scanning…" : "Ready"}
            </Box>
          ) : (
            <Box as="span" fontSize="11px" fontWeight="700" color="#0f766e" bg="#f0fdfa" px={3} py="5px" borderRadius="full" border="1px solid #99f6e4">
              Best prices with prescription
            </Box>
          )}
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2.5} mt={3}>
          {[
            { title: "1. Upload", desc: "Camera or file upload" },
            { title: "2. AI reads", desc: "Medicines detected in seconds" },
            { title: "3. Review", desc: "Compare pharmacies and continue" },
          ].map((step) => (
            <Box key={step.title} bg="#f0fdfa" border="1px solid #ccfbf1" borderRadius="12px" px={3} py={2.5}>
              <Text fontSize="11px" fontWeight="800" color="#0f766e" textTransform="uppercase" letterSpacing="0.06em" m={0}>
                {step.title}
              </Text>
              <Text fontSize="12px" color="#475569" m={0} mt={1}>
                {step.desc}
              </Text>
            </Box>
          ))}
        </SimpleGrid>

        <Box mt={3}>
            <Button
              size="xs" variant="ghost" color="#0f766e" fontWeight="700" fontSize="11px"
              leftIcon={prescriptionHistoryLoading ? undefined : <FiClock size={11} />}
              isLoading={prescriptionHistoryLoading}
              onClick={fetchPrescriptionHistory}
              px={2} h={7}
            >
              {prescriptionHistoryLoaded ? `${prescriptionHistory.length} saved prescription${prescriptionHistory.length !== 1 ? "s" : ""}` : "Load saved prescriptions"}
            </Button>
            {prescriptionHistoryLoaded && prescriptionHistory.length > 0 && (
              <HStack spacing={2} overflowX="auto" pb={1} mt={2}>
                {prescriptionHistory.map((p, idx) => (
                  <Box
                    key={idx}
                    minW="150px"
                    px={3} py={2.5}
                    borderRadius="12px"
                    border="1px solid #ccfbf1"
                    bg="#f0fdfa"
                    cursor="pointer"
                    _hover={{ bg: "#ccfbf1", borderColor: "#0f766e" }}
                    transition="all 0.12s"
                    onClick={() => handleUseSavedPrescription(p)}
                  >
                    <HStack spacing={1.5} mb={1}>
                      <Icon as={FiFileText} color="#0f766e" boxSize={3} />
                      <Text fontSize="11px" fontWeight="800" color="#0f172a" m={0} noOfLines={1}>
                        {p.originalName || p.fileName || "Prescription"}
                      </Text>
                    </HStack>
                    <Text fontSize="10px" color="#64748b" m={0}>
                      {p.uploadedAt ? new Date(p.uploadedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : ""}
                    </Text>
                    <Box as="span" fontSize="10px" fontWeight="700" color="#0f766e" mt={1} display="block">Tap to use →</Box>
                  </Box>
                ))}
              </HStack>
            )}
            {prescriptionHistoryLoaded && prescriptionHistory.length === 0 && (
              <Text fontSize="11px" color="#94a3b8" m={0} mt={1}>No saved prescriptions found</Text>
            )}
          </Box>

        {hasRxMeds && files.length === 0 && (
          <Flex align="center" gap={2} bg="#fff7ed" p={3} borderRadius="10px" mt={3}>
            <Icon as={FiAlertTriangle} color="#c2410c" boxSize={4} flexShrink={0} />
            <Text fontSize="xs" color="#9a3412" m={0} lineHeight="1.4">
              One or more medicines require a prescription. Upload before finding pharmacies.
            </Text>
          </Flex>
        )}
      </Box>

      {/* Upload area — compact row after upload, full drop zone otherwise */}
      <Box px={{ base: 5, lg: 6 }} pb={{ base: 4, lg: 4 }}>
        {files.length > 0 && !dropZoneOpen ? (
          /* Compact: single file row + reupload button */
          <Flex
            align="center"
            gap={3}
            px={4}
            py={3}
            bg="#f0fdfa"
            borderRadius="12px"
            border="1.5px solid #99f6e4"
          >
            <Icon as={files[0].type?.startsWith("image/") ? FiFileText : FiFile} color="#0f766e" boxSize={4} flexShrink={0} />
            <Box flex={1} minW={0}>
              <Text fontSize="sm" fontWeight="600" color="#0f172a" noOfLines={1} m={0}>
                {files.length === 1 ? files[0].name : `${files.length} files attached`}
              </Text>
              {files.length === 1 && (
                <Text fontSize="xs" color="#64748b" m={0} mt="1px">
                  {(files[0].size / 1024 / 1024).toFixed(1)} MB
                </Text>
              )}
            </Box>
            <Button
              size="xs"
              h="28px"
              px={3}
              variant="ghost"
              color="#0f766e"
              _hover={{ bg: "#ccfbf1" }}
              leftIcon={<FiRotateCcw size={11} />}
              fontWeight="700"
              borderRadius="full"
              fontSize="11px"
              onClick={() => setDropZoneOpen(true)}
            >
              Reupload
            </Button>
          </Flex>
        ) : (
          /* Full drop zone */
          <>
            <Box
              border={`1.5px dashed ${hasRxMeds ? "#fb923c" : "#e2e8f0"}`}
              borderRadius="14px"
              py={{ base: 7, lg: 8 }}
              px={{ base: 5, lg: 6 }}
              textAlign="center"
              bg={hasRxMeds ? "#fff9f5" : "#fafafa"}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <VStack spacing={4}>
                <Box
                  w={{ base: "46px", xl: "52px" }}
                  h={{ base: "46px", xl: "52px" }}
                  borderRadius="14px"
                  bg="linear-gradient(180deg, #eefaf8 0%, #e2f7f2 100%)"
                  display="flex" alignItems="center" justifyContent="center"
                  boxShadow="inset 0 1px 0 rgba(255,255,255,0.9)"
                >
                  <Icon as={FiUploadCloud} boxSize={{ base: 10, xl: 10 }} color="#0f766e" />
                </Box>
                <Box>
                  <Text fontWeight="700" color="#0f172a" fontSize="sm" m={0}>Upload your prescription</Text>
                  <Text fontSize="xs" color="#94a3b8" m={0} mt="2px">JPG, PNG or PDF · Up to 5 files</Text>
                </Box>
                <Flex gap={2} justify="center" align="center" wrap="wrap">
                  <Button
                    size="sm" h="36px"
                    bg="linear-gradient(135deg, #0f766e 0%, #0d9488 100%)" color="white"
                    _hover={{ bg: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)" }}
                    borderRadius="10px"
                    leftIcon={<FiCamera size={14} />}
                    fontWeight="700" fontSize="sm"
                    minW="136px"
                    isLoading={isCameraStarting}
                    onClick={startCameraCapture}
                  >
                    Use camera
                  </Button>
                  <Button
                    size="sm" h="36px"
                    variant="outline" borderColor="#e2e8f0"
                    color="#475569"
                    borderRadius="10px"
                    leftIcon={<FiUploadCloud size={14} />}
                    fontWeight="700" fontSize="xs"
                    minW="118px"
                    onClick={() => document.getElementById("upload-prescription-input")?.click()}
                  >
                    Upload file
                  </Button>
                  {files.length > 0 && (
                    <Button
                      size="sm" h="36px"
                      variant="ghost" color="#94a3b8"
                      borderRadius="10px"
                      fontWeight="700" fontSize="xs"
                      minW="90px"
                      onClick={() => setDropZoneOpen(false)}
                    >
                      Cancel
                    </Button>
                  )}
                </Flex>
              </VStack>
              <input
                id="upload-prescription-input"
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                multiple
                style={{ display: "none" }}
                onChange={handleFileInput}
              />
            </Box>
          </>
        )}
      </Box>

      {/* Scan results */}
      {(scanLoading || (scanSuggestions.length > 0 && !allScanAdded) || scanError || scanNotice) && (
        <Box borderTop="1px solid #f1f5f9" px={{ base: 5, lg: 6 }} py={scanLoading ? 0 : 3.5}>
          {scanLoading ? (
            <ScanningAnimation />
          ) : (
            <>
              <Flex align={{ base: "flex-start", md: "center" }} justify="space-between" mb={3} gap={3} wrap="wrap">
                <Box>
                  <Text fontWeight="800" color="#0f172a" fontSize="sm" m={0}>
                    {allScanAdded
                      ? `Detected ${scanSuggestions.length} medicines and added all of them`
                      : `Detected ${scanSuggestions.length} medicines, ${pendingScanSuggestions.length} left to review`}
                  </Text>
                  <Text fontSize="11px" color="#64748b" m={0} mt="3px">
                    Reading → detecting → adding medicines to your basket
                  </Text>
                </Box>
                {pendingScanSuggestions.length > 0 && (
                  <Box as="span" fontSize="11px" fontWeight="700" color="#4338ca" bg="#eef2ff" px={3} py="4px" borderRadius="full">
                    Review and add
                  </Box>
                )}
              </Flex>

              {scanError && <Text fontSize="xs" color="#b91c1c" mb={2} m={0}>{scanError}</Text>}
              {!scanError && scanSuggestions.length === 0 && files.length > 0 && (
                <Text fontSize="xs" color="#64748b" m={0}>No medicines detected. Add manually below.</Text>
              )}
            </>
          )}

          {!scanLoading && (
            <Stack spacing={2.5} maxH="340px" overflowY="auto" pr={0.5}>
              {scanSuggestions.map((med, index) => {
                const matched = Boolean(med?.matched);
                const normalizedMed = matched
                  ? med
                  : {
                      _id: `scan-${med?.extracted_name || "medicine"}-${index}`,
                      product_name: med?.extracted_name || "Scanned medicine",
                      name: med?.extracted_name || "Scanned medicine",
                      composition: med?.extracted_salt_name || med?.extracted_generic_name || "",
                      extracted_written_name: med?.extracted_written_name || med?.extracted_name || "",
                      extracted_brand_name: med?.extracted_brand_name || "",
                      extracted_generic_name: med?.extracted_generic_name || "",
                      extracted_salt_name: med?.extracted_salt_name || "",
                      extracted_dosage_form: med?.extracted_dosage_form || "",
                      extracted_strength: med?.extracted_strength || "",
                      extracted_route: med?.extracted_route || "",
                      extracted_dose_amount: med?.extracted_dose_amount || "",
                      extracted_frequency: med?.extracted_frequency || "",
                      extracted_duration: med?.extracted_duration || "",
                      extracted_quantity: med?.extracted_quantity || "",
                      extracted_instruction: med?.extracted_instruction || "",
                      extracted_uncertain: Boolean(med?.extracted_uncertain),
                      prescription_required: true,
                      matched: false,
                      extractedFromPrescription: true,
                      qty: Number(med?.extracted_quantity) || 1,
                    };
                const added = addedMeds.some((item) => getMedId(item) === getMedId(normalizedMed));
                if (added) return null;

                const nameMismatch = Boolean(med?.name_mismatch);
                const statusLabel = med?.auto_add_eligible
                  ? { text: "Auto-added", color: "#166534", bg: "#dcfce7" }
                  : nameMismatch
                  ? { text: "Name differs — review", color: "#92400e", bg: "#fff7ed" }
                  : med?.extracted_uncertain
                  ? { text: "Unclear", color: "#92400e", bg: "#fef3c7" }
                  : !matched
                  ? { text: "Needs match", color: "#4338ca", bg: "#eef2ff" }
                  : null;

                // Derived display values
                const displayName = matched ? (med.product_name || med.Name || med.name) : (med.extracted_name || "Unmatched");
                const writtenName = med?.extracted_written_name && med.extracted_written_name !== displayName ? med.extracted_written_name : null;
                const salt = med?.extracted_salt_name || (matched && (med.composition || med.key_ingredients)) || "";
                const strength = med?.extracted_strength || "";
                const dosageForm = med?.extracted_dosage_form || "";
                const frequency = med?.extracted_frequency || "";
                const duration = med?.extracted_duration || "";
                const rxQty = med?.extracted_quantity || med?.extracted_dose_amount || "";
                const price = med?.mrp != null && Number(med?.mrp) > 0 ? Number(med.mrp).toFixed(0) : null;
                const dotColor = nameMismatch ? "#d97706" : med?.extracted_uncertain ? "#d97706" : "#0f766e";

                return (
                  <Box
                    key={`${med?.product_id || med?.Name || med?.extracted_name || "scan"}-${index}`}
                    px={3.5} py={3}
                    bg="#ffffff"
                    border={nameMismatch ? "1px solid #fed7aa" : "1px solid #e8edf3"}
                    borderRadius="14px"
                    boxShadow="0 2px 10px rgba(15,23,42,0.03)"
                    _hover={{ borderColor: nameMismatch ? "#fb923c" : "#dbe5ee", boxShadow: "0 8px 20px rgba(15,23,42,0.06)" }}
                    transition="all 0.12s"
                  >
                    <Flex align="flex-start" justify="space-between" gap={3}>
                      <Flex align="flex-start" gap={2.5} minW={0} flex={1}>
                        <Box w="8px" h="8px" borderRadius="full" bg={dotColor} flexShrink={0} mt="5px" />
                        <Box minW={0} flex={1}>
                          {/* Name row */}
                          <Flex align="center" gap={2} mb="2px" wrap="wrap">
                            <Text fontWeight="800" color="#0f172a" noOfLines={1} m={0} fontSize="sm" flex={1} minW={0}>
                              {displayName}
                            </Text>
                            {price && (
                              <Box as="span" fontSize="11px" fontWeight="800" color="#0f766e" bg="#f0fdfa" border="1px solid #99f6e4" px={2} py="2px" borderRadius="full" flexShrink={0}>
                                ₹{price}
                              </Box>
                            )}
                          </Flex>

                          {/* Written name on Rx (when different) */}
                          {writtenName && (
                            <Text fontSize="11px" color="#64748b" m={0} mb="3px">
                              <Box as="span" fontWeight="700" color="#92400e">On Rx:</Box> {writtenName}
                            </Text>
                          )}

                          {/* Salt / composition */}
                          {salt && (
                            <Text fontSize="11px" color="#475569" m={0} mb="3px" noOfLines={1}>
                              <Box as="span" fontWeight="700">Salt:</Box> {String(salt).split(" + ").slice(0, 2).join(" + ")}
                            </Text>
                          )}

                          {/* Dosage details row */}
                          <Flex gap={1.5} wrap="wrap" mt="3px">
                            {dosageForm && (
                              <Box as="span" fontSize="10px" fontWeight="600" color="#475569" bg="#f8fafc" border="1px solid #edf2f7" px={2} py="2px" borderRadius="full">{dosageForm}</Box>
                            )}
                            {strength && (
                              <Box as="span" fontSize="10px" fontWeight="600" color="#475569" bg="#f8fafc" border="1px solid #edf2f7" px={2} py="2px" borderRadius="full">{strength}</Box>
                            )}
                            {frequency && (
                              <Box as="span" fontSize="10px" fontWeight="700" color="#0f766e" bg="#f0fdfa" border="1px solid #ccfbf1" px={2} py="2px" borderRadius="full">{frequency}</Box>
                            )}
                            {duration && (
                              <Box as="span" fontSize="10px" fontWeight="600" color="#64748b" bg="#f8fafc" border="1px solid #edf2f7" px={2} py="2px" borderRadius="full">{duration}</Box>
                            )}
                            {rxQty && (
                              <Box as="span" fontSize="10px" fontWeight="600" color="#64748b" bg="#f8fafc" border="1px solid #edf2f7" px={2} py="2px" borderRadius="full">Qty: {rxQty}</Box>
                            )}
                            {statusLabel && (
                              <Box as="span" fontSize="10px" fontWeight="700" color={statusLabel.color} bg={statusLabel.bg} px={2} py="2px" borderRadius="full">{statusLabel.text}</Box>
                            )}
                          </Flex>
                        </Box>
                      </Flex>

                      <Button
                        size="sm"
                        h="34px"
                        px={4}
                        bg="#0f766e"
                        color="white"
                        _hover={{ bg: "#0d9488" }}
                        variant="solid"
                        borderRadius="full"
                        fontWeight="700"
                        fontSize="12px"
                        flexShrink={0}
                        alignSelf="flex-start"
                        onClick={() => handleAddMedicine(normalizedMed)}
                      >
                        Add
                      </Button>
                    </Flex>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      )}
    </>
  );

  const mapSection = (
    <Box
      bg="white"
      boxShadow="0 14px 34px rgba(15,23,42,0.07)"
      border="1px solid #e7edf3"
      borderRadius="20px"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      minH={{ base: "520px", xl: "900px" }}
      maxH={{ base: "none", xl: "900px" }}
      h={{ xl: "900px" }}
    >
      <Box px={{ base: 5, lg: 6 }} pt={{ base: 5, lg: 5 }} pb={5} flexShrink={0}>
        <Box mb={4}>
          <Box
            bg="#f8fafc"
            border="1px solid #e7edf3"
            borderRadius="16px"
            px={4}
            py={4}
          >
            <Flex direction="column" gap={3}>
              <Box>
                <Flex align="center" justify="space-between" gap={3} wrap="wrap" mb={1}>
                  <Text fontWeight="900" color="#0f172a" fontSize={{ base: "lg", xl: "xl" }} m={0} letterSpacing="-0.02em">
                    Nearby pharmacies
                  </Text>
                  <Box
                    as="span"
                    fontSize="12px"
                    fontWeight="800"
                    color="#0f766e"
                    bg="#ecfeff"
                    px={3}
                    py="6px"
                    borderRadius="full"
                    border="1px solid #ccfbf1"
                    whiteSpace="nowrap"
                    flexShrink={0}
                  >
                    {nearbyLoading && !nearbyPharmaciesResolved
                      ? "Finding near you…"
                      : `${nearbyPharmacies.length} nearby ${nearbyPharmacies.length === 1 ? "pharmacy" : "pharmacies"}`}
                  </Box>
                </Flex>
                <Text fontSize="xs" color="#7b8798" m={0}>
                  See nearby coverage now, then compare live offers once your list is ready.
                </Text>
              </Box>

              <Box
                bg="white"
                border="1px solid #e7edf3"
                borderRadius="14px"
                px={4}
                py={3}
              >
                <Flex align="center" justify="space-between" gap={3} mb={2} wrap="wrap">
                  <Text
                    fontSize="10px"
                    fontWeight="800"
                    color="#64748b"
                    textTransform="uppercase"
                    letterSpacing="0.1em"
                    m={0}
                  >
                    Search radius
                  </Text>
                  <Box
                    as="span"
                    fontSize="12px"
                    fontWeight="900"
                    color="#0f766e"
                    bg="#ecfeff"
                    border="1px solid #ccfbf1"
                    px={2.5}
                    py="3px"
                    borderRadius="full"
                  >
                    {searchRadiusKm} km
                  </Box>
                </Flex>
                <Box
                  as="input"
                  type="range"
                  className="pillstack-radius-slider"
                  min={3}
                  max={20}
                  step={1}
                  value={searchRadiusKm}
                  onChange={(e) => setSearchRadiusKm(Number(e.target.value))}
                  w="100%"
                  display="block"
                  sx={{
                    "--pillstack-slider-progress": `${((searchRadiusKm - 3) / (20 - 3)) * 100}%`,
                  }}
                />
                <Flex justify="space-between" mt={1.5}>
                  <Text fontSize="11px" color="#94a3b8" fontWeight="700" m={0}>3 km</Text>
                  <Text fontSize="11px" color="#94a3b8" fontWeight="700" m={0}>10 km</Text>
                  <Text fontSize="11px" color="#94a3b8" fontWeight="700" m={0}>20 km</Text>
                </Flex>
              </Box>
            </Flex>
          </Box>
        </Box>

        {nearbyPharmacies.length > 1 ? (
          <Box mt={3} overflowX="auto" sx={{ "&::-webkit-scrollbar": { height: "6px" }, "&::-webkit-scrollbar-thumb": { background: "#d7e3ee", borderRadius: "999px" } }}>
            <HStack spacing={3} minW="max-content" pb={1}>
              {nearbyPharmacies.slice(0, 8).map((pharmacy) => (
                <Box
                  key={pharmacy.id}
                  minW="170px"
                  maxW="190px"
                  px={3}
                  py={2.5}
                  borderRadius="14px"
                  border="1px solid #e7edf3"
                  bg="#ffffff"
                  boxShadow="0 4px 12px rgba(15,23,42,0.04)"
                >
                  <Text fontSize="sm" fontWeight="800" color="#0f172a" m={0} noOfLines={1}>
                    {pharmacy.name}
                  </Text>
                  <HStack spacing={2} mt={1} wrap="wrap">
                    {pharmacy.distanceKm ? (
                      <Box as="span" fontSize="10px" fontWeight="700" color="#0f766e" bg="#ecfeff" px={2} py="3px" borderRadius="full">
                        {Number(pharmacy.distanceKm).toFixed(1)} km
                      </Box>
                    ) : null}
                    <Box
                      as="span"
                      fontSize="10px"
                      fontWeight="700"
                      color={pharmacy.isOpen ? "#166534" : "#92400e"}
                      bg={pharmacy.isOpen ? "#dcfce7" : "#fef3c7"}
                      px={2}
                      py="3px"
                      borderRadius="full"
                    >
                      {pharmacy.isOpen ? "Open" : "Check hours"}
                    </Box>
                  </HStack>
                </Box>
              ))}
            </HStack>
          </Box>
        ) : null}
      </Box>

      <Box
        ref={mapViewportRef}
        flex={1}
        h={{ base: "340px", md: "380px" }}
        minH={{ base: "340px", md: "380px", xl: "460px" }}
        overflow="hidden"
        position="relative"
      >
        <Box position="absolute" top={0} right={0} bottom={0} left={0}>
          <PharmacyMap
            height="100%"
            isLoggedIn={true}
            embeddedMapOnly
            hideSearchOverlay
            hideDeliveryBubble
            searchRadiusKm={searchRadiusKm}
            onLocationChange={({ address, coords }) => {
              // Ignore the well-known Delhi default that PharmacyMap broadcasts
              // before its own geolocation completes.
              const isDelhiDefault =
                Array.isArray(coords) &&
                coords.length === 2 &&
                Math.abs(coords[0] - 77.209) < 0.001 &&
                Math.abs(coords[1] - 28.6139) < 0.001;
              if (isDelhiDefault) return;
              // If user has saved addresses, don't let PharmacyMap's GPS
              // overwrite the chosen saved address — only update coords for the map.
              const hasSaved = Array.isArray(fullUser?.DeliveryAddress) && fullUser.DeliveryAddress.length > 0;
              setSelection((prev) => ({
                ...(prev || {}),
                ...(!hasSaved || !prev?.deliveryAddress ? { deliveryAddress: address } : {}),
                deliveryCoords: coords,
                coords,
              }));
            }}
            onPharmaciesChange={(pharmacies) => {
              const mapped = (pharmacies || []).map((pharmacy) => ({
                id: pharmacy.id,
                name: pharmacy.name,
                address: pharmacy.fullAddress || pharmacy.locality || "",
                coords: pharmacy.coords || null,
                distanceKm: pharmacy.distance || null,
                isFavorite: Boolean(pharmacy.isFavorite),
                isPreferred: Boolean(pharmacy.isPreferred),
                isOpen: Boolean(pharmacy.isOpen),
                openStatus: pharmacy.openStatus || "",
              }));
              setNearbyPharmacies(mapped);
              setNearbyPharmaciesResolved(true);
            }}
          />
        </Box>
        {!nearbyLoading && nearbyPharmaciesResolved && nearbyPharmacies.length === 0 ? (
          <Box
            position="absolute"
            inset="20px"
            bg="rgba(255,255,255,0.94)"
            border="1px solid #e7edf3"
            borderRadius="18px"
            boxShadow="0 18px 40px rgba(15,23,42,0.12)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            px={6}
          >
            <Box>
              <Text fontSize="18px" fontWeight="800" color="#0f172a" m={0}>
                No pharmacies in this range
              </Text>
              <Text fontSize="13px" color="#64748b" mt={2} m={0} lineHeight="1.7">
                Increase the search radius or update the delivery location to see more pharmacy coverage.
              </Text>
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );

  const pharmacyDecisionSection =
    hasAttemptedPharmacyCheck || pharmacyMatchesLoading || pharmacyMatchesError || pharmacyMatches.length ? (
      <Box
        bg="white"
        boxShadow="0 1px 2px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)"
        borderRadius="16px"
        overflow="hidden"
      >
        <Box px={{ base: 5, lg: 6 }} pt={{ base: 5, lg: 6 }} pb={4} borderBottom="1px solid #f1f5f9">
          <Flex align={{ base: "flex-start", md: "center" }} justify="space-between" gap={4} wrap="wrap">
            <Box>
              <Text fontWeight="800" color="#0f172a" fontSize="lg" m={0} letterSpacing="-0.02em">Choose your pharmacy</Text>
              <Text fontSize="sm" color="#64748b" m={0} mt="2px">
                {pharmacyMatchesLoading
                  ? "Checking live availability…"
                  : pharmacyMatches.length
                    ? `${pharmacyMatches.length} connected pharmacie${pharmacyMatches.length > 1 ? "s" : ""} responded with live pricing`
                    : "No responses yet — only connected pharmacies reply"}
              </Text>
            </Box>
            {pharmacySelectionIsFromResults ? (
              <Flex align="center" gap={2} bg="#f0fdfa" border="1px solid #99f6e4" px={3} py={1.5} borderRadius="full">
                <Box w="6px" h="6px" borderRadius="full" bg="#0f766e" />
                <Text fontSize="12px" fontWeight="700" color="#0f766e" m={0}>
                  {selection?.pharmacyName || "Selected"}
                </Text>
              </Flex>
            ) : null}
          </Flex>
        </Box>

        <Box px={{ base: 5, lg: 6 }} py={5}>
          <Stack spacing={4}>
            <PharmacySearchState
              hasMedicines={addedMeds.length > 0}
              locationReady={hasDeliveryLocation}
              radiusKm={searchRadiusKm}
              loading={pharmacyMatchesLoading}
              error={pharmacyMatchesError}
              matchesCount={pharmacyMatches.length}
            />
            {pharmacyMatches.length ? (
              <PharmacyOfferCards
                pharmacies={pharmacyMatches}
                onChoose={handleChoosePharmacyOffer}
                selectedPharmacyId={selection?.pharmacyId || null}
              />
            ) : null}
            {requiresRxButNoUpload ? (
              <Flex align="center" gap={2} bg="#fff1f2" px={4} py={3} borderRadius="10px">
                <Icon as={FiAlertTriangle} color="#b91c1c" boxSize={4} />
                <Text fontSize="sm" color="#b91c1c" m={0}>
                  Upload a prescription for Rx medicines before continuing.
                </Text>
              </Flex>
            ) : null}
            {pharmacyMatches.length ? (
              <Flex
                align={{ base: "stretch", md: "center" }}
                justify="space-between"
                gap={4}
                wrap="wrap"
                p={4}
                borderRadius="14px"
                border="1px solid #e6eef5"
                bg={pharmacySelectionIsFromResults && !requiresRxButNoUpload ? "#f8fffd" : "#f8fafc"}
              >
                <Box minW={0} flex={1}>
                  <Text fontSize="11px" fontWeight="800" color="#94a3b8" textTransform="uppercase" letterSpacing="0.08em" m={0}>
                    Ready to continue
                  </Text>
                  <Text fontSize="sm" fontWeight="800" color="#0f172a" mt="6px" m={0}>
                    {pharmacySelectionIsFromResults
                      ? selection?.pharmacyName || "Selected pharmacy"
                      : "Select one pharmacy to lock this draft"}
                  </Text>
                  <Text fontSize="sm" color="#64748b" mt="4px" m={0}>
                    {pharmacySelectionIsFromResults
                      ? "We’ll carry this pharmacy forward into order review and payment."
                      : "Choose the best match above to continue with pricing, fulfillment, and payment."}
                  </Text>
                </Box>

                <Button
                  minW={{ base: "full", md: "280px" }}
                  size="md"
                  h="46px"
                  bg={
                    !pharmacySelectionIsFromResults || requiresRxButNoUpload
                      ? "#f1f5f9"
                      : "linear-gradient(135deg, #0f766e 0%, #0d9488 100%)"
                  }
                  color={!pharmacySelectionIsFromResults || requiresRxButNoUpload ? "#94a3b8" : "white"}
                  _hover={{
                    bg: !pharmacySelectionIsFromResults || requiresRxButNoUpload
                      ? "#f1f5f9"
                      : "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)",
                    transform: pharmacySelectionIsFromResults && !requiresRxButNoUpload ? "translateY(-1px)" : "none",
                    boxShadow: pharmacySelectionIsFromResults && !requiresRxButNoUpload ? "0 6px 20px rgba(15,118,110,0.35)" : "none",
                  }}
                  _active={{ transform: "none", boxShadow: "none" }}
                  rightIcon={<FiArrowRight />}
                  onClick={handleContinue}
                  isLoading={isSubmitting}
                  isDisabled={!pharmacySelectionIsFromResults || requiresRxButNoUpload}
                  borderRadius="10px"
                  fontWeight="700"
                  fontSize="sm"
                  letterSpacing="0.01em"
                >
                  {!pharmacySelectionIsFromResults
                    ? "Select a pharmacy to continue"
                    : requiresRxButNoUpload
                    ? "Upload prescription to continue"
                    : "Continue to order review"}
                </Button>
              </Flex>
            ) : null}
          </Stack>
        </Box>
      </Box>
    ) : null;

  return checkingAuth ? null : (
    <>
      {!embedded && (
        <>
          <Sidebar
            onLogout={handleLogout}
            collapsed={sidebarCollapsed}
            onToggleSidebar={handleSidebarToggle}
            mobileOpen={mobileSidebarOpen}
            onCloseMobile={() => setMobileSidebarOpen(false)}
            brandName={theme.brandName}
          />
          <div
            style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}
          >
            <WithSubnavigation
              username={username}
              openChangePasswordModal={() => {}}
              onLogout={handleLogout}
              notifications={fullUser.Notifications || []}
              onToggleSidebar={handleSidebarToggle}
              collapsed={sidebarCollapsed}
              sidebarOffset={sidebarCollapsed ? "74px" : "248px"}
              savedAddresses={fullUser.DeliveryAddress || []}
            />
          </div>
        </>
      )}
      <Box
        mt={embedded ? 0 : "90px"}
        px={embedded ? 0 : { base: "18px", md: "24px", xl: "32px" }}
        ml={embedded ? 0 : { base: 0, md: sidebarCollapsed ? "74px" : "248px" }}
        transition="margin-left 0.3s ease"
        minH={embedded ? undefined : "100vh"}
        bg={embedded ? undefined : "#f8fafc"}
        backgroundImage={embedded ? undefined : "radial-gradient(ellipse 80% 300px at 50% 0%, rgba(15,118,110,0.06) 0%, transparent 100%)"}
        pb={embedded ? undefined : { base: "88px", md: 0 }}
      >
        {!embedded ? (
          <Flex align={{ base: "flex-start", xl: "center" }} justify="space-between" mb={5} wrap="wrap" gap={4}>
            <Stack spacing={1.5}>
              <Text fontSize={{ base: "2xl", xl: "3xl" }} fontWeight="900" lineHeight="1.05" color="#0f172a" m={0}>
                Order from nearby pharmacies
              </Text>
              <Text fontSize="sm" color="#64748b" maxW="760px" m={0}>
                Upload a prescription or add medicines manually, then compare nearby pharmacy matches in the next step.
              </Text>
            </Stack>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<FiArrowLeft />}
              onClick={() => navigate("/home")}
            >
              Back
            </Button>
          </Flex>
        ) : null}

        <Box
          display={{ base: "flex", xl: "grid" }}
          flexDirection={{ base: "column" }}
          gridTemplateColumns={{ xl: "minmax(620px, 0.58fr) minmax(440px, 0.42fr)", "2xl": "minmax(700px, 0.6fr) minmax(500px, 0.4fr)" }}
          gap={{ base: 3, xl: 5, "2xl": 6 }}
          alignItems="start"
        >
          {/* Left panel: Prescription + Medicine in ONE card */}
          <Box
            bg="white"
            boxShadow="0 14px 34px rgba(15,23,42,0.07)"
            border="1px solid #e7edf3"
            borderRadius="20px"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            minW={0}
          >
            {/* Prescription section */}
            {uploadSection}

            {/* Divider */}
            <Box borderTop="1px solid #f1f5f9" />

            {/* Medicine section — grows to fill remaining height */}
            <Box flex={1} display="flex" flexDirection="column">
              {addMedicinesSection}
            </Box>
          </Box>

          {/* Right panel: Map — must be flex so mapSection fills full height */}
          <Box
            minW={0}
            display="flex"
            flexDirection="column"
            position={{ base: "static", xl: "sticky" }}
            top={{ xl: "104px" }}
            alignSelf="start"
          >
            {mapSection}
          </Box>
        </Box>

        {/* Summary strip */}
        <Box mt={{ base: 3, xl: 4 }}>
          {requestSummaryStrip}
        </Box>

        {/* Pharmacy results */}
        {pharmacyDecisionSection ? (
          <Box mt={{ base: 3, xl: 4 }} ref={pharmacySectionRef}>
            {pharmacyDecisionSection}
          </Box>
        ) : null}
      </Box>

      <Modal
        isOpen={locationModal.isOpen}
        onClose={locationModal.onClose}
        size="xl"
        isCentered
      >
        <ModalOverlay bg="rgba(15,23,42,0.45)" backdropFilter="blur(4px)" />
        <ModalContent
          borderRadius="20px"
          overflow="hidden"
          boxShadow="0 28px 60px rgba(15,23,42,0.32)"
          maxW="640px"
        >
          <ModalHeader bg="#0f172a" color="white" pb={3} pt={4} fontSize="xl">
            Update location & pharmacy
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody
            p={5}
            bg="linear-gradient(180deg, #f8fafc 0%, #ffffff 40%)"
          >
            <Stack spacing={2}>
              <Text fontWeight="800" color="#0f172a" fontSize="md" mb={2}>
                Current selection
              </Text>
              {/* Delivery address section with Google Places */}
              <Box
                border="1px solid #e2e8f0"
                borderRadius="14px"
                bg="white"
                overflow="hidden"
                mb={1}
              >
                {/* Current address display */}
                {editedAddress && (
                  <Flex align="center" gap={2} px={3} py={2.5} bg="#f0fdf9" borderBottom="1px solid #e2e8f0">
                    <Icon as={FiMapPin} color="#0f766e" boxSize={4} flexShrink={0} />
                    <Text fontSize="13px" fontWeight="700" color="#0f172a" m={0} flex={1} noOfLines={2}>
                      {editedAddress}
                    </Text>
                  </Flex>
                )}

                {/* Search input */}
                <Box px={3} py={2}>
                  <Flex align="center" gap={2}>
                    <Icon as={FiSearch} color="#94a3b8" boxSize={4} flexShrink={0} />
                    <Input
                      ref={modalAddressInputRef}
                      placeholder="Search for area, street, locality..."
                      value={modalPlaceQuery}
                      onChange={(e) => {
                        setModalPlaceQuery(e.target.value);
                        modalSearchPlaces(e.target.value);
                      }}
                      size="sm"
                      variant="unstyled"
                      fontSize="13px"
                    />
                  </Flex>
                </Box>

                {/* Use current location */}
                <Box
                  as="button"
                  type="button"
                  onClick={modalResetToGps}
                  display="flex"
                  alignItems="center"
                  gap={2.5}
                  w="100%"
                  px={3}
                  py={2}
                  bg="transparent"
                  _hover={{ bg: "#f0fdf9" }}
                  transition="background 0.12s"
                  borderTop="1px solid #f1f5f9"
                  cursor="pointer"
                  disabled={modalDetectingGps}
                >
                  <Icon as={FiCrosshair} color="#0f766e" boxSize={4} />
                  <Box textAlign="left">
                    <Text fontSize="13px" fontWeight="700" color="#0f766e" m={0}>
                      {modalDetectingGps ? "Detecting..." : "Use current location"}
                    </Text>
                    <Text fontSize="11px" color="#94a3b8" m={0}>Using GPS</Text>
                  </Box>
                </Box>

                {/* Google Places suggestions */}
                {modalPlaceQuery.trim().length >= 2 && (
                  <Box borderTop="1px solid #f1f5f9">
                    <Text fontSize="10px" fontWeight="800" color="#94a3b8" textTransform="uppercase" letterSpacing="0.08em" m={0} px={3} pt={2} pb={1}>
                      Search results
                    </Text>
                    <Stack spacing={0} maxH="160px" overflowY="auto">
                      {modalPlacesLoading ? (
                        <Text fontSize="12px" color="#64748b" px={3} py={2} m={0}>Searching...</Text>
                      ) : modalPlaceSuggestions.length > 0 ? (
                        modalPlaceSuggestions.map((p) => (
                          <Box
                            key={p.place_id}
                            as="button"
                            type="button"
                            onClick={() => modalSelectPlace(p.place_id, p.description)}
                            display="flex"
                            alignItems="center"
                            gap={2.5}
                            w="100%"
                            px={3}
                            py={2}
                            bg="transparent"
                            _hover={{ bg: "#f8fafc" }}
                            transition="background 0.12s"
                            cursor="pointer"
                            textAlign="left"
                          >
                            <Icon as={FiMapPin} color="#94a3b8" boxSize={3.5} flexShrink={0} />
                            <Text fontSize="12px" fontWeight="600" color="#334155" m={0} noOfLines={2}>
                              {p.description}
                            </Text>
                          </Box>
                        ))
                      ) : (
                        <Text fontSize="12px" color="#64748b" px={3} py={2} m={0}>No results found</Text>
                      )}
                    </Stack>
                  </Box>
                )}

                {/* Save this address prompt */}
                {modalLastSelectedAddr && !modalIsAddrSaved(modalLastSelectedAddr) && (
                  <Flex
                    align="center"
                    gap={2.5}
                    px={3}
                    py={2.5}
                    bg="#f0fdf9"
                    borderTop="1px solid #e2e8f0"
                  >
                    <Icon as={FiBookmark} color="#0f766e" boxSize={4} flexShrink={0} />
                    <Text fontSize="12px" fontWeight="600" color="#334155" m={0} flex={1} noOfLines={1}>
                      Save for future deliveries?
                    </Text>
                    <Box
                      as="button"
                      type="button"
                      onClick={() => modalSaveAddress(modalLastSelectedAddr)}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      px={2.5}
                      py={1}
                      bg="white"
                      border="1px solid #99f6e4"
                      borderRadius="8px"
                      fontSize="11px"
                      fontWeight="800"
                      color="#0f766e"
                      cursor="pointer"
                      _hover={{ bg: "#ccfbf1" }}
                      transition="all 0.12s"
                      flexShrink={0}
                      disabled={modalSavingAddress}
                    >
                      <Icon as={modalSavingAddress ? FiCheck : FiBookmark} boxSize={3} />
                      {modalSavingAddress ? "Saving..." : "Save"}
                    </Box>
                  </Flex>
                )}

                {/* Saved addresses */}
                {(fullUser?.DeliveryAddress || []).length > 0 && (
                  <Box borderTop="1px solid #f1f5f9">
                    <Text fontSize="10px" fontWeight="800" color="#94a3b8" textTransform="uppercase" letterSpacing="0.08em" m={0} px={3} pt={2} pb={1}>
                      Saved addresses
                    </Text>
                    <Stack spacing={0} maxH="120px" overflowY="auto" pb={1}>
                      {(fullUser?.DeliveryAddress || []).map((addr, i) => (
                        <Box
                          key={`modal-saved-${i}`}
                          as="button"
                          type="button"
                          onClick={() => modalSelectSavedAddress(addr)}
                          display="flex"
                          alignItems="center"
                          gap={2.5}
                          w="100%"
                          px={3}
                          py={2}
                          bg={editedAddress === addr ? "#f0fdf9" : "transparent"}
                          _hover={{ bg: "#f8fafc" }}
                          transition="background 0.12s"
                          cursor="pointer"
                          textAlign="left"
                        >
                          <Icon as={FiClock} color="#94a3b8" boxSize={3.5} flexShrink={0} />
                          <Text fontSize="12px" fontWeight="600" color="#334155" m={0} noOfLines={1}>
                            {addr}
                          </Text>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>

              <Text fontSize="xs" color="#475569" mb={1}>
                Tap the map to reposition the delivery pin.
              </Text>
              <Flex gap={3} wrap="wrap" align="stretch">
                <Box
                  flex={{ base: "1 1 100%", lg: "0.42" }}
                  minW="260px"
                  border="1px solid #e2e8f0"
                  borderRadius="16px"
                  p={3}
                  bg="linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)"
                  boxShadow="0 16px 30px rgba(15,23,42,0.12)"
                >
                  <Flex justify="space-between" align="center" mb={0}>
                    <Text fontWeight="800" color="#0f172a">
                      Nearby pharmacies
                    </Text>
                    <IconButton
                      aria-label="Refresh pharmacies"
                      icon={<FiRotateCcw />}
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        fetchNearbyPharmacies(
                          editedDeliveryCoords ||
                            editedPharmacyCoords || [77.209, 28.6139]
                        )
                      }
                      isLoading={nearbyLoading}
                    />
                  </Flex>
                  <Input
                    placeholder="Search pharmacy"
                    size="sm"
                    mb={2}
                    value={pharmacySearch}
                    onChange={(e) => setPharmacySearch(e.target.value)}
                    bg="white"
                    borderRadius="12px"
                    boxShadow="inset 0 1px 2px rgba(15,23,42,0.06)"
                  />
                  <Stack spacing={2} maxH="200px" overflowY="auto">
                    {nearbyPharmacies
                      .filter((p) =>
                        p.name
                          .toLowerCase()
                          .includes(pharmacySearch.toLowerCase())
                      )
                      .map((p) => {
                        const isSelected = editedPharmacyId === p.id;
                        return (
                          <Flex
                            key={p.id}
                            justify="space-between"
                            align="center"
                            border="1px solid"
                            borderColor={isSelected ? "#0f766e" : "#e2e8f0"}
                            borderRadius="12px"
                            p={2}
                            bg={isSelected ? "#e6fffa" : "white"}
                            cursor="pointer"
                            onClick={() => {
                              setEditedPharmacyId(p.id);
                              setEditedPharmacyName(p.name);
                              setEditedPharmacyAddress(p.address || "");
                              setEditedPharmacyCoords(p.coords);
                              setPharmacySearch("");
                              setNearbyPharmacies([]);
                            }}
                            boxShadow={
                              isSelected
                                ? "0 10px 20px rgba(15,118,110,0.12)"
                                : "none"
                            }
                            transition="all 0.12s ease"
                          >
                            <Box>
                              <Box
                                as="span"
                                display="block"
                                fontWeight="800"
                                color="#0f172a"
                                fontSize="sm"
                                noOfLines={1}
                              >
                                {p.name}
                              </Box>
                              <Box
                                as="span"
                                display="block"
                                fontSize="xs"
                                color="#475569"
                                noOfLines={1}
                              >
                                {p.address || "Address not available"}
                              </Box>
                              <Flex mt={1} gap={2} wrap="wrap">
                                {p.isOpen ? (
                                  <Box
                                    as="span"
                                    fontSize="10px"
                                    fontWeight="700"
                                    color="#166534"
                                    bg="#dcfce7"
                                    px={2}
                                    py={0.5}
                                    borderRadius="999px"
                                  >
                                    Open
                                  </Box>
                                ) : (
                                  <Box
                                    as="span"
                                    fontSize="10px"
                                    fontWeight="700"
                                    color="#991b1b"
                                    bg="#fee2e2"
                                    px={2}
                                    py={0.5}
                                    borderRadius="999px"
                                  >
                                    Closed
                                  </Box>
                                )}
                                {p.isPreferred ? (
                                  <Box
                                    as="span"
                                    fontSize="10px"
                                    fontWeight="700"
                                    color="#0f766e"
                                    bg="#ccfbf1"
                                    px={2}
                                    py={0.5}
                                    borderRadius="999px"
                                  >
                                    First choice
                                  </Box>
                                ) : null}
                                {p.isFavorite && !p.isPreferred ? (
                                  <Box
                                    as="span"
                                    fontSize="10px"
                                    fontWeight="700"
                                    color="#92400e"
                                    bg="#fef3c7"
                                    px={2}
                                    py={0.5}
                                    borderRadius="999px"
                                  >
                                    Favorite
                                  </Box>
                                ) : null}
                              </Flex>
                              {p.openStatus ? (
                                <Box
                                  as="span"
                                  display="block"
                                  mt={1}
                                  fontSize="10px"
                                  color="#64748b"
                                >
                                  {p.openStatus}
                                </Box>
                              ) : null}
                            </Box>
                          </Flex>
                        );
                      })}
                    {!nearbyLoading && !nearbyPharmacies.length && (
                      <Text fontSize="sm" color="#475569">
                        No pharmacies found nearby.
                      </Text>
                    )}
                  </Stack>
                </Box>
                <Box
                  flex={{ base: "1 1 100%", lg: "0.58" }}
                  minW="300px"
                  minH="320px"
                  border="1px solid #d7e3f0"
                  borderRadius="16px"
                  position="relative"
                  bg="white"
                  boxShadow="0 16px 30px rgba(15,23,42,0.12)"
                >
                  <Box position="absolute" top={1.5} right={1.5} bottom={1.5} left={1.5}>
                    <MapPreviewInline
                      height="100%"
                      borderRadius="12px"
                      deliveryCoords={editedDeliveryCoords}
                      pharmacyCoords={editedPharmacyCoords}
                      editable
                      onSelectDelivery={(coords) =>
                        setEditedDeliveryCoords(coords)
                      }
                    />
                  </Box>
                </Box>
              </Flex>
            </Stack>
          </ModalBody>
          <ModalFooter gap={3} bg="#f8fafc">
            <Button variant="ghost" onClick={locationModal.onClose}>
              Cancel
            </Button>
            <Button colorScheme="teal" onClick={applyLocationChange}>
              Save changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={cameraModal.isOpen}
        onClose={closeCameraCapture}
        size="xl"
        isCentered
      >
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(6px)" />
        <ModalContent borderRadius="22px" overflow="hidden">
          <ModalHeader bg="#0f172a" color="white" pb={3}>
            Capture prescription
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody bg="#020617" p={4}>
            <Stack spacing={3}>
              <Box
                borderRadius="18px"
                overflow="hidden"
                bg="black"
                border="1px solid rgba(148,163,184,0.22)"
                boxShadow="0 20px 40px rgba(2,6,23,0.35)"
              >
                <Box
                  as="video"
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  w="100%"
                  maxH="70vh"
                  objectFit="cover"
                  bg="black"
                />
              </Box>
              <Text fontSize="sm" color="#cbd5e1">
                {cameraError
                  ? cameraError
                  : "Frame the prescription clearly. Rear camera is preferred on mobile."}
              </Text>
            </Stack>
          </ModalBody>
          <ModalFooter bg="#0f172a" gap={3}>
            <Button variant="ghost" color="white" onClick={closeCameraCapture}>
              Cancel
            </Button>
            <Button colorScheme="teal" leftIcon={<FiCamera />} onClick={capturePrescriptionPhoto}>
              Capture Photo
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UploadPrescription;
